/**
 * LOCs Page - Manage Letters of Credit
 */

import { useState, useEffect, Fragment, useMemo, useCallback } from 'react'
import { useAccount } from 'wagmi'
import { useConnectModal } from '@rainbow-me/rainbowkit'
import { Transition, Dialog } from '@headlessui/react'
import { SEO, generateBreadcrumbSchema } from '../components/common/SEO'
import { Card, CardContent } from '../components/common/Card'
import { Button } from '../components/common/Button'
import { Input } from '../components/common/Input'
import { useLOCDetails, useIssueLOC, useRedeemLOC, useExtendLOC, useCancelLOC, useModifyLOCCollateral, useCollateralFactor, useCreditedTokenConfig, useMaxLocDuration } from '../hooks/useLOC'
import { CONFIGURED_LOC_PAIRS, getPairConfig, getAvailableCreditedTokens } from '@contracts/locPairs'
import { useUserLOCsFromEvents, useBeneficiaryLOCsFromEvents, LOCEvent } from '../hooks/useLOCEvents'
import { useSingleLOCHealth } from '../hooks/useLOCHealth'
import { useBulkLOCStatusPolling, useExpiringLOCsAlert } from '../hooks/useLOCStatusPolling'
import { useAllAccountCollateralBalances } from '../hooks/useVault'
import { useTokenPrices } from '../hooks/useTokenPrice'
import { useENSResolution } from '../hooks/useENS'
import { useCollateralizableAllowance, useNeedsCollateralizableApproval, useModifyCollateralizableAllowance } from '../hooks/useCollateralizableAllowance'
import { getContractAddress } from '@contracts/addresses'
// Note: Anvil uses account-based collateral, not vault IDs
import { formatAddress, formatTokenAmount, formatDate } from '../utils/format'
import { LOCStatus } from '../types/contracts'
import { DocumentIcon } from '../assets/icons'
import { InfoBox } from '../components/common/InfoBox'
import { WalletConnectIllustration } from '@bitcoin-com/react-components/src/components/mini-illustrations'
import { LiquidationRiskIndicator } from '../components/vault/LiquidationRiskIndicator'
import { isAddress, formatUnits } from 'viem'

// Bank partner addresses — lending partners users can apply to
const LENDING_PARTNERS: {
  key: string; name: string; address: string; description: string;
  country: string; flag: string; interestRate: string; ltv: string; slug: string;
}[] = [
  {
    key: 'AMDAX',
    name: 'AMDAX',
    address: '0x717d52e84eF30875De52834603c112675CeDB7CA',
    description: 'Licensed digital asset exchange & custodian',
    country: 'Netherlands',
    flag: '🇳🇱',
    interestRate: '8.5 - 12%',
    ltv: 'Up to 50%',
    slug: 'amdax',
  },
  {
    key: 'EMPOWERMINT',
    name: 'Empowermint',
    address: '0x765365f5DF83A79001229f4E76881976039DB8F5',
    description: 'Digital asset lending partner',
    country: 'United States',
    flag: '🇺🇸',
    interestRate: '9 - 14%',
    ltv: 'Up to 60%',
    slug: 'empowermint',
  },
]
import { toast } from 'react-hot-toast'
import { TokenSelector } from '../components/vault/TokenSelector'
import { SUPPORTED_TOKENS, Token, parseTokenAmount } from '@contracts/tokens'
import { HowItWorksFlow, StepProgress } from '../components/common/HowItWorksFlow'
import { MINIMUM_COLLATERAL_USD } from '../config/constants'

type ViewMode = 'list' | 'create'

export function LOCsPage() {
  const { address, isConnected } = useAccount()
  const { openConnectModal } = useConnectModal()
  // Use event-based LOC fetching instead of getUserLOCs (which doesn't exist)
  const { locs: locEvents, isLoading, refetch } = useUserLOCsFromEvents(address)
  const { locs: beneficiaryLocEvents, isLoading: isLoadingBeneficiary } = useBeneficiaryLOCsFromEvents(address)

  // Tab: 'creator' = LOCs I created, 'beneficiary' = LOCs where I'm the beneficiary
  const [roleTab, setRoleTab] = useState<'creator' | 'beneficiary'>('creator')

  const activeLocEvents = roleTab === 'creator' ? locEvents : beneficiaryLocEvents
  const locIds = activeLocEvents.map(loc => loc.id)
  const isLoadingLocs = roleTab === 'creator' ? isLoading : isLoadingBeneficiary

  // Build a map from locId → LOCEvent for health calculations
  const locEventMap = useMemo(() => {
    const map = new Map<string, LOCEvent>()
    ;[...locEvents, ...beneficiaryLocEvents].forEach(e => map.set(e.id.toString(), e))
    return map
  }, [locEvents, beneficiaryLocEvents])

  // Poll LOC statuses for expiry detection (MAP-20)
  // Callbacks wrapped in useCallback to prevent infinite re-render loops (#4)
  const handleStatusChange = useCallback((updates: import('../hooks/useLOCStatusPolling').LOCStatusUpdate[]) => {
    updates.forEach(u => toast(`LOC #${u.id} is now ${u.currentStatus}`, { icon: '🔄' }))
    refetch()
  }, [refetch])
  const handleExpiringAlert = useCallback((expiring: import('../hooks/useLOCEvents').LOCEvent[]) => {
    toast(`⏰ ${expiring.length} LOC(s) expiring within 24 hours!`, { icon: '⏰' })
  }, [])
  useBulkLOCStatusPolling(activeLocEvents, handleStatusChange)
  useExpiringLOCsAlert(activeLocEvents, handleExpiringAlert)

  const { issueLOC, isPending, isSuccess, isError, error, hash } = useIssueLOC()
  
  // Note: Anvil uses account-based collateral
  const hasAccount = !!address
  
  // Fetch collateral balances and prices
  const { data: collateralData, isLoading: isLoadingCollateral } = useAllAccountCollateralBalances(address)
  const { data: prices, isLoading: isLoadingPrices } = useTokenPrices(Object.values(SUPPORTED_TOKENS))
  
  // Calculate total collateral value in USD
  let totalCollateralValueUSD = 0
  if (collateralData && prices) {
    Object.entries(collateralData).forEach(([tokenAddress, { available }]) => {
      // Match token address, excluding native ETH (only WETH can be in vault)
      const token = Object.values(SUPPORTED_TOKENS).find(
        (t) => t.address.toLowerCase() === tokenAddress.toLowerCase() && !t.isNative
      )
      if (token && available > 0n) {
        const balance = parseFloat(formatUnits(available, token.decimals))
        const price = prices[token.symbol] || 0
        totalCollateralValueUSD += balance * price
      }
    })
  } else if (!isLoadingCollateral && !isLoadingPrices) {
    // Only warn if loading is complete but data is still missing
  }

  // Collateral data loaded
  // Note: Prices are static fallback values (see useTokenPrice.ts)
  // TODO: Implement real-time price feeds via backend proxy or Chainlink oracle

  const hasLowCollateral = totalCollateralValueUSD > 0 && totalCollateralValueUSD < MINIMUM_COLLATERAL_USD
  
  // Estimate total LOC value (in production, calculate from actual LOC details)
  const hasActiveLOCs = (locIds?.length || 0) > 0
  const estimatedLOCValueUSD = hasActiveLOCs ? totalCollateralValueUSD * 0.5 : 0
  
  // Calculate user progress through the flow
  const userProgress: StepProgress = {
    fundVault: totalCollateralValueUSD >= MINIMUM_COLLATERAL_USD, // Has deposited sufficient collateral
    issueLOC: hasActiveLOCs, // Has issued at least one LOC
    applyLoan: false, // TODO: Track loan requests (for now false)
    loanStatus: undefined // TODO: Track loan status
  }
  
  // View mode: 'list' shows LOCs, 'create' shows inline form
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  
  // Form state
  const [formData, setFormData] = useState({
    vaultId: '0', // Default to 0 for account-based system
    beneficiary: '',
    memo: '',
    locId: '',
    expiryDate: '',
    creditedAmount: '',
  })
  
  // Token selection state (must be declared before calculating available tokens)
  const [selectedCreditedAsset, setSelectedCreditedAsset] = useState<Token | undefined>(SUPPORTED_TOKENS['USDC'])
  const [selectedCollateralAsset, setSelectedCollateralAsset] = useState<Token | undefined>(undefined)
  
  // Check if user has any deposit for the selected collateral token
  // This prevents approval failures when user tries to approve a token they haven't deposited
  const selectedCollateralBalance = useMemo(() => {
    if (!selectedCollateralAsset || !collateralData) return 0n
    const tokenData = Object.entries(collateralData).find(
      ([addr]) => addr.toLowerCase() === selectedCollateralAsset.address.toLowerCase()
    )
    return tokenData ? tokenData[1].available : 0n
  }, [selectedCollateralAsset, collateralData])
  
  const hasSelectedCollateralDeposit = selectedCollateralBalance > 0n
  
  // Get available collateral tokens — all tokens configured in Anvil pairs
  // (not just deposited ones, so users can explore before depositing)
  const availableCollateralTokens = useMemo(() => {
    const uniqueAddresses = [...new Set(CONFIGURED_LOC_PAIRS.map(p => p.collateralAddress.toLowerCase()))]
    return uniqueAddresses
      .map(addr => Object.values(SUPPORTED_TOKENS).find(
        t => t.address.toLowerCase() === addr && !t.isNative
      ))
      .filter((t): t is Token => !!t)
  }, [])
  
  // Auto-select first available collateral token when data loads
  // IMPORTANT: In Anvil's model, LOC value token and collateral token must be the SAME
  useEffect(() => {
    if (availableCollateralTokens.length > 0) {
      // If no token selected, or selected token is not in available list, select first available
      const isCurrentTokenAvailable = selectedCollateralAsset && availableCollateralTokens.some(
        t => t.address.toLowerCase() === selectedCollateralAsset.address.toLowerCase()
      )
      
      if (!selectedCollateralAsset || !isCurrentTokenAvailable) {
        const firstToken = availableCollateralTokens[0]
        setSelectedCollateralAsset(firstToken)
        // Auto-select first available credited token for this collateral
        const availableCredited = getAvailableCreditedTokens(firstToken.address)
        if (availableCredited.length > 0) {
          const creditedToken = Object.values(SUPPORTED_TOKENS).find(
            t => t.address.toLowerCase() === availableCredited[0].address.toLowerCase()
          )
          if (creditedToken) {
            setSelectedCreditedAsset(creditedToken)
          }
        }
      }
    }
  }, [availableCollateralTokens.length, collateralData])
  
  // Get available credited tokens for selected collateral
  const availableCreditedTokens = useMemo(() => {
    if (!selectedCollateralAsset) return []
    const available = getAvailableCreditedTokens(selectedCollateralAsset.address)
    return available.map(({ address: addr }) =>
      Object.values(SUPPORTED_TOKENS).find(
        t => t.address.toLowerCase() === addr.toLowerCase()
      )
    ).filter(Boolean) as Token[]
  }, [selectedCollateralAsset])
  
  // Auto-select first credited token when collateral changes
  useEffect(() => {
    if (selectedCollateralAsset && availableCreditedTokens.length > 0) {
      const currentValid = availableCreditedTokens.some(
        t => t.address.toLowerCase() === selectedCreditedAsset?.address.toLowerCase()
      )
      if (!currentValid) {
        setSelectedCreditedAsset(availableCreditedTokens[0])
      }
    }
  }, [selectedCollateralAsset, availableCreditedTokens])
  
  // Fetch collateral factor for selected token pair (from contract)
  const { collateralFactor, isLoading: isLoadingCollateralFactor } = useCollateralFactor(
    selectedCollateralAsset?.address,
    selectedCreditedAsset?.address
  )
  
  // Get static config as fallback
  const staticPairConfig = useMemo(() => {
    if (!selectedCollateralAsset || !selectedCreditedAsset) return null
    return getPairConfig(selectedCollateralAsset.address, selectedCreditedAsset.address)
  }, [selectedCollateralAsset, selectedCreditedAsset])
  
  // Use contract data if available, otherwise fallback to static config
  const effectiveCollateralFactor = useMemo(() => {
    if (collateralFactor && collateralFactor.creationCollateralFactorBasisPoints > 0) {
      return collateralFactor
    }
    if (staticPairConfig) {
      return {
        creationCollateralFactorBasisPoints: staticPairConfig.creationFactorBps,
        collateralFactorBasisPoints: staticPairConfig.maintenanceFactorBps,
        liquidatorIncentiveBasisPoints: staticPairConfig.liquidatorIncentiveBps,
      }
    }
    return null
  }, [collateralFactor, staticPairConfig])
  
  // Calculate maximum LTV based on collateral factor
  // creationCollateralFactorBasisPoints is the max LTV in bps (e.g. 5600 = 56% max LTV).
  // The multiplier (required collateral value / credited value) is the inverse:
  // 5600 bps → 10000 / 5600 = 1.786x collateral needed.
  const collateralFactorMultiplier = effectiveCollateralFactor && effectiveCollateralFactor.creationCollateralFactorBasisPoints > 0
    ? 10000 / effectiveCollateralFactor.creationCollateralFactorBasisPoints
    : 1.5 // Default to 150% collateral (≈66% max LTV) if not loaded
  
  const maxLTVPercent = (1 / collateralFactorMultiplier) * 100 // e.g., 100 / 1.5 = 66.67%
  const absoluteMaxLTV = maxLTVPercent // User can go up to 100% of protocol max
  const extremeZoneStart = maxLTVPercent * 0.98 // 98% of max (e.g., 65.3%)
  
  // LTV slider state (50% is recommended safe level for crypto assets)
  const [ltvPercentage, setLtvPercentage] = useState(50)
  // Flag to prevent LTV ↔ amount oscillation (#6)
  const [ltvSource, setLtvSource] = useState<'slider' | 'amount'>('slider')
  
  // Calculate recommended LOC amount based on LTV and collateral
  const maxLOCValue = totalCollateralValueUSD * (ltvPercentage / 100)
  
  // LTV risk level - dynamically scaled based on protocol max
  const getLTVRisk = (ltv: number) => {
    const safeZone = maxLTVPercent * 0.60 // 0-60% of max
    const moderateZone = maxLTVPercent * 0.75 // 60-75% of max
    const highZone = maxLTVPercent * 0.90 // 75-90% of max
    const dangerZone = maxLTVPercent * 0.98 // 90-98% of max
    
    if (ltv <= safeZone) return { level: 'safe', color: 'green', message: 'Safe borrowing level - low liquidation risk' }
    if (ltv <= moderateZone) return { level: 'moderate', color: 'yellow', message: 'Moderate risk - monitor collateral regularly' }
    if (ltv <= highZone) return { level: 'high', color: 'orange', message: 'High risk - vulnerable to price drops' }
    if (ltv <= dangerZone) return { level: 'danger', color: 'red', message: 'Danger - very close to liquidation!' }
    return { level: 'extreme', color: 'black', message: '⛔ EXTREME RISK - Liquidation almost guaranteed on any price movement!' }
  }
  
  const ltvRisk = getLTVRisk(ltvPercentage)
  
  // Calculate required collateral USD for real-time validation (Major #3 & #5)
  const requiredCollateralUSDForValidation = useMemo(() => {
    if (!formData.creditedAmount || !selectedCreditedAsset || !prices) {
      return 0
    }
    
    const creditedAmountNum = parseFloat(formData.creditedAmount)
    if (isNaN(creditedAmountNum) || creditedAmountNum <= 0) {
      return 0
    }
    
    const creditedPrice = prices[selectedCreditedAsset.symbol]
    if (!creditedPrice || creditedPrice <= 0) {
      return 0
    }
    
    return creditedAmountNum * creditedPrice * collateralFactorMultiplier
  }, [formData.creditedAmount, selectedCreditedAsset, prices, collateralFactorMultiplier])
  
  // Check if user has insufficient collateral for the requested LOC amount
  const hasInsufficientCollateral = requiredCollateralUSDForValidation > 0 && requiredCollateralUSDForValidation > totalCollateralValueUSD
  
  // Update LTV slider when amount changes (one-way: amount → slider, skip if slider initiated)
  useEffect(() => {
    if (ltvSource === 'slider') return // Skip if slider caused this change
    if (formData.creditedAmount && selectedCreditedAsset && prices && totalCollateralValueUSD > 0) {
      const locValueUSD = parseFloat(formData.creditedAmount) * (prices[selectedCreditedAsset.symbol] || 0)
      const calculatedLTV = (locValueUSD / totalCollateralValueUSD) * 100
      if (Math.abs(calculatedLTV - ltvPercentage) > 0.5) {
        setLtvPercentage(Math.min(calculatedLTV, absoluteMaxLTV))
      }
    }
  }, [formData.creditedAmount, selectedCreditedAsset, prices, totalCollateralValueUSD, absoluteMaxLTV]) // removed ltvPercentage dep
  
  // ENS resolution for beneficiary
  const { address: ensResolvedAddress, isLoading: ensLoading, error: ensError } = useENSResolution(formData.beneficiary)
  
  // Fetch credited token config (min/max per LOC, global limits)
  const { config: creditedTokenConfig, isLoading: _isLoadingCreditedConfig } = useCreditedTokenConfig(
    selectedCreditedAsset?.address
  )
  
  // Fetch max LOC duration
  const { maxDurationSeconds } = useMaxLocDuration()
  
  // Check collateralizable allowance for LOC issuance
  const locContractAddress = getContractAddress('LetterOfCreditProxy')
  
  // Calculate required collateral amount based on form inputs
  // This is used to check if the user has approved ENOUGH collateral (not just any amount)
  const requiredCollateralAmount = useMemo(() => {
    if (!formData.creditedAmount || !selectedCreditedAsset || !selectedCollateralAsset || !prices) {
      return undefined
    }
    
    // Parse credited amount
    const creditedAmountNum = parseFloat(formData.creditedAmount)
    if (isNaN(creditedAmountNum) || creditedAmountNum <= 0) {
      return undefined
    }
    
    // Get collateral factor multiplier (required collateral value / credited value)
    // creationCollateralFactorBasisPoints is max LTV (5600 bps = 56% LTV = 1.786x multiplier)
    const cfMultiplier = (collateralFactor?.creationCollateralFactorBasisPoints &&
                          !isNaN(collateralFactor.creationCollateralFactorBasisPoints) &&
                          collateralFactor.creationCollateralFactorBasisPoints > 0)
      ? 10000 / collateralFactor.creationCollateralFactorBasisPoints
      : 1.5 // Default 150% collateral
    
    // Get prices
    const creditedPrice = prices[selectedCreditedAsset.symbol]
    const collateralPrice = prices[selectedCollateralAsset.symbol]
    
    if (!creditedPrice || !collateralPrice || creditedPrice <= 0 || collateralPrice <= 0) {
      return undefined
    }
    
    // Calculate required collateral in tokens
    const creditedValueUSD = creditedAmountNum * creditedPrice
    const requiredCollateralUSD = creditedValueUSD * cfMultiplier
    const requiredCollateralTokens = requiredCollateralUSD / collateralPrice
    
    // Add 1% buffer to account for price fluctuations
    const bufferedCollateral = requiredCollateralTokens * 1.01
    
    return parseTokenAmount(bufferedCollateral.toFixed(18), selectedCollateralAsset.decimals)
  }, [formData.creditedAmount, selectedCreditedAsset, selectedCollateralAsset, prices, collateralFactor])
  
  // Use amount-based allowance check (not just binary > 0)
  const { 
    needsApproval,
    currentAllowance: _collateralizableAllowance,
    isLoading: isLoadingAllowance,
  } = useNeedsCollateralizableApproval(
    address,
    selectedCollateralAsset?.address,
    locContractAddress,
    requiredCollateralAmount
  )
  
  // Also get refetch function for after approval
  const { 
    refetch: refetchAllowance
  } = useCollateralizableAllowance(
    address,
    selectedCollateralAsset?.address,
    locContractAddress
  )
  
  // Derived state: is collateral approved for this LOC amount?
  const hasCollateralizableAllowance = !needsApproval && !isLoadingAllowance
  
  // Hook to approve collateral use for LOCs
  const {
    approveUnlimited,
    isPending: isApprovingCollateral,
    isSuccess: isApprovalSuccess,
    isError: isApprovalError,
    error: approvalError
  } = useModifyCollateralizableAllowance()
  
  // State to track approval verification
  const [isVerifyingApproval, setIsVerifyingApproval] = useState(false)
  
  // Track the token that was approved (for Critical #2)
  const [approvedCollateralToken, setApprovedCollateralToken] = useState<string | undefined>(undefined)
  
  // Refetch allowance after successful approval (with proper verification)
  useEffect(() => {
    if (isApprovalSuccess) {
      setIsVerifyingApproval(true)
      
      // Remember which token was approved
      if (selectedCollateralAsset?.address) {
        setApprovedCollateralToken(selectedCollateralAsset.address.toLowerCase())
      }
      
      // Wait for refetch to complete before showing success
      refetchAllowance()
        .then(() => {
          setIsVerifyingApproval(false)
          toast.success('Collateral approved for LOC issuance!')
        })
        .catch((err) => {
          setIsVerifyingApproval(false)
          toast.error('Approval submitted but verification failed. Please refresh.')
          console.error('🔴 [LOCsPage] Failed to verify approval:', err)
        })
    }
  }, [isApprovalSuccess, refetchAllowance, selectedCollateralAsset?.address])
  
  // Handle approval errors
  useEffect(() => {
    if (isApprovalError) {
      console.error('🔴 [LOCsPage] Collateral approval failed:', {
        error: approvalError,
        message: approvalError?.message || 'Unknown error',
        selectedCollateralAsset: selectedCollateralAsset?.symbol,
        locContractAddress,
      })
      // Show more specific error if available
      const errorMsg = approvalError?.message?.includes('User rejected') 
        ? 'Transaction rejected by user'
        : approvalError?.message?.slice(0, 100) || 'Unknown error'
      toast.error(`Failed to approve collateral: ${errorMsg}`)
    }
  }, [isApprovalError, approvalError, selectedCollateralAsset?.symbol, locContractAddress])
  
  // Critical #2: Warn if user changes collateral token after approval
  useEffect(() => {
    if (approvedCollateralToken && selectedCollateralAsset?.address) {
      if (approvedCollateralToken.toLowerCase() !== selectedCollateralAsset.address.toLowerCase()) {
        toast('⚠️ Collateral token changed. You may need to approve again.', { icon: '⚠️' })
        // Clear the approved token state so the warning doesn't keep showing
        setApprovedCollateralToken(undefined)
      }
    }
  }, [selectedCollateralAsset?.address, approvedCollateralToken])
  
  // Status filter state ('all' or specific status)
  const [statusFilter, setStatusFilter] = useState<'all' | LOCStatus>('all')
  
  // Pagination state — P2 task #4.10
  const PAGE_SIZE = 10
  const [currentPage, setCurrentPage] = useState(1)
  
  // Reset pagination when switching role tabs (#15)
  useEffect(() => { setCurrentPage(1) }, [roleTab])
  
  const [formErrors, setFormErrors] = useState({
    vaultId: '',
    beneficiary: '',
    memo: '',
    locId: '',
    expiryDate: '',
    creditedAmount: '',
    creditedAsset: '',
    collateralAsset: '',
  })

  // Handle paste from clipboard
  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText()
      setFormData(prev => ({ ...prev, beneficiary: text.trim() }))
      toast.success('Address pasted from clipboard')
    } catch (err) {
      toast.error('Failed to read from clipboard. Please paste manually.')
    }
  }
  
  // Reset form to initial state
  const resetForm = () => {
    setFormData({
      vaultId: '0',
      beneficiary: '',
      memo: '',
      locId: '',
      expiryDate: '',
      creditedAmount: '',
    })
    setSelectedCreditedAsset(SUPPORTED_TOKENS['USDC'])
    // IMPORTANT: Only select collateral from tokens user has actually deposited!
    // Setting to undefined will trigger auto-select from availableCollateralTokens
    setSelectedCollateralAsset(undefined)
    setFormErrors({
      vaultId: '',
      beneficiary: '',
      memo: '',
      locId: '',
      expiryDate: '',
      creditedAmount: '',
      creditedAsset: '',
      collateralAsset: '',
    })
  }
  
  // Handle successful LOC issuance
  useEffect(() => {
    if (isSuccess && hash) {
      toast.success('LOC issued successfully!', { icon: '✓' })
      // Don't switch view mode yet - show success state inline
      
      // Refetch LOCs after a short delay
      setTimeout(() => {
        refetch()
      }, 2000)
    }
  }, [isSuccess, hash, refetch])

  // Handle LOC issuance error
  useEffect(() => {
    if (isError && error) {
      toast.error(`Failed to issue LOC: ${error.message}`)
    }
  }, [isError, error])

  const validateForm = () => {
    const errors = {
      vaultId: '',
      beneficiary: '',
      memo: '',
      locId: '',
      expiryDate: '',
      creditedAmount: '',
      creditedAsset: '',
      collateralAsset: '',
    }
    
    let isValid = true

    // Validate beneficiary address (supports ENS resolution)
    const trimmedBeneficiary = formData.beneficiary.trim()
    if (!trimmedBeneficiary) {
      errors.beneficiary = 'Beneficiary address is required'
      isValid = false
    } else {
      // Check if it's an ENS name or direct address
      const isENSName = trimmedBeneficiary.endsWith('.eth')
      
      if (isENSName) {
        // For ENS names, check if resolution succeeded
        if (ensLoading) {
          errors.beneficiary = 'Resolving ENS name...'
          isValid = false
        } else if (ensError || !ensResolvedAddress) {
          errors.beneficiary = 'Failed to resolve ENS name'
          isValid = false
        }
        // If resolved successfully, ensResolvedAddress will be used
      } else {
        // For direct addresses, validate format (trim whitespace)
        if (!isAddress(trimmedBeneficiary)) {
          errors.beneficiary = 'Invalid Ethereum address format'
          isValid = false
        }
      }
    }
    
    // Validate credited asset
    if (!selectedCreditedAsset) {
      errors.creditedAsset = 'Please select a credited token'
      isValid = false
    }

    // Validate credited amount
    if (!formData.creditedAmount) {
      errors.creditedAmount = 'LOC value is required'
      isValid = false
    } else if (isNaN(Number(formData.creditedAmount)) || Number(formData.creditedAmount) <= 0) {
      errors.creditedAmount = 'Amount must be greater than 0'
      isValid = false
    } else if (selectedCreditedAsset && creditedTokenConfig &&
        typeof creditedTokenConfig.minPerDynamicLOC === 'bigint' &&
        typeof creditedTokenConfig.maxPerDynamicLOC === 'bigint' &&
        creditedTokenConfig.maxPerDynamicLOC > 0n) {
      try {
        const parsed = parseTokenAmount(formData.creditedAmount, selectedCreditedAsset.decimals)
        if (parsed < creditedTokenConfig.minPerDynamicLOC) {
          const minDisplay = formatUnits(creditedTokenConfig.minPerDynamicLOC, selectedCreditedAsset.decimals)
          errors.creditedAmount = `Minimum LOC amount is ${minDisplay} ${selectedCreditedAsset.symbol}`
          isValid = false
        } else if (parsed > creditedTokenConfig.maxPerDynamicLOC) {
          const maxDisplay = formatUnits(creditedTokenConfig.maxPerDynamicLOC, selectedCreditedAsset.decimals)
          errors.creditedAmount = `Maximum LOC amount is ${maxDisplay} ${selectedCreditedAsset.symbol}`
          isValid = false
        }
      } catch {
        // parseTokenAmount may throw on invalid input — other validators handle that
      }
    }

    // Validate collateral asset
    if (!selectedCollateralAsset) {
      errors.collateralAsset = 'Please select a collateral token'
      isValid = false
    }

    // Note: Collateral amount is not validated - Anvil uses account-based collateral
    // The contract automatically checks the user's total account collateral when issuing the LOC

    // Validate expiry date and time (datetime-local format: YYYY-MM-DDTHH:mm)
    if (!formData.expiryDate) {
      errors.expiryDate = 'Expiry date and time is required'
      isValid = false
    } else {
      // Check if expiry is in the future
      const expiryDatetime = new Date(formData.expiryDate)
      const now = new Date()
      if (expiryDatetime <= now) {
        errors.expiryDate = 'Expiry must be in the future'
        isValid = false
      }
      // Validate against maxLocDuration (#10, #12)
      if (maxDurationSeconds && isValid) {
        const maxExpiry = now.getTime() / 1000 + maxDurationSeconds
        if (expiryDatetime.getTime() / 1000 > maxExpiry) {
          const maxDate = new Date(maxExpiry * 1000)
          errors.expiryDate = `Expiry exceeds max duration. Latest allowed: ${maxDate.toLocaleDateString()}`
          isValid = false
        }
      }
    }

    setFormErrors(errors)
    return { isValid, errors }
  }

  const handleIssueLOC = async () => {
    
    // Check account
    if (!hasAccount) {
      console.error('🔴 [LOCsPage] No account found')
      toast.error('Please connect your wallet')
      return
    }
    
    // Check asset selection first
    if (!selectedCreditedAsset) {
      console.error('🔴 [LOCsPage] No credited asset selected')
      toast.error('Please select a credited token')
      return
    }
    
    if (!selectedCollateralAsset) {
      console.error('🔴 [LOCsPage] No collateral asset selected')
      toast.error('Please select a collateral token')
      return
    }
    
    // Check collateral approval
    if (!hasCollateralizableAllowance) {
      console.error('🔴 [LOCsPage] Collateral not approved for LOC use')
      toast.error('Please approve collateral use first. Click the "Approve Collateral Use" button.')
      return
    }
    
    // Check if collateral deposited
    if (!collateralData || totalCollateralValueUSD === 0) {
      console.error('🔴 [LOCsPage] No collateral deposited')
      toast.error('Please deposit collateral to your vault first!')
      return
    }
    
    // Validate form — use returned errors directly (not stale formErrors state)
    const { isValid, errors: validationErrors } = validateForm()
    
    if (!isValid) {
      console.error('🔴 [LOCsPage] Form validation failed')
      const firstError = Object.entries(validationErrors).find(([_, error]) => error)
      if (firstError) {
        toast.error(`Validation error: ${firstError[1]}`)
      } else {
        toast.error('Please check all required fields')
      }
      return
    }

    try {
      // Validate prices are available (not loading, not NaN)
      if (!prices || isLoadingPrices) {
        toast.error('Loading prices... Please wait.')
        console.error('🔴 [LOCsPage] Prices not loaded yet')
        return
      }

      // Validate against contract min/max limits (P1 task #4.8)
      if (creditedTokenConfig && selectedCreditedAsset &&
          typeof creditedTokenConfig.minPerDynamicLOC === 'bigint' &&
          typeof creditedTokenConfig.maxPerDynamicLOC === 'bigint' &&
          creditedTokenConfig.maxPerDynamicLOC > 0n) {
        const creditedAmount_parsed = parseTokenAmount(formData.creditedAmount, selectedCreditedAsset.decimals)
        if (creditedAmount_parsed < creditedTokenConfig.minPerDynamicLOC) {
          const minDisplay = formatUnits(creditedTokenConfig.minPerDynamicLOC, selectedCreditedAsset.decimals)
          toast.error(`Amount too small. Minimum LOC amount is ${minDisplay} ${selectedCreditedAsset.symbol}`)
          return
        }
        if (creditedAmount_parsed > creditedTokenConfig.maxPerDynamicLOC) {
          const maxDisplay = formatUnits(creditedTokenConfig.maxPerDynamicLOC, selectedCreditedAsset.decimals)
          toast.error(`Amount too large. Maximum LOC amount is ${maxDisplay} ${selectedCreditedAsset.symbol}`)
          return
        }
      }

      // Parse datetime-local value (format: YYYY-MM-DDTHH:mm)
      const expiryDatetime = new Date(formData.expiryDate)
      const expirationTimestamp = Math.floor(expiryDatetime.getTime() / 1000)

      // Use resolved ENS address if available, otherwise use direct address
      const beneficiaryInput = formData.beneficiary.trim()
      const beneficiary = (beneficiaryInput.endsWith('.eth') ? ensResolvedAddress : beneficiaryInput) as `0x${string}`
      const creditedAmount = parseTokenAmount(formData.creditedAmount, selectedCreditedAsset.decimals)
      const creditedTokenAddress = selectedCreditedAsset.address
      const collateralTokenAddress = selectedCollateralAsset.address
      
      // Calculate required collateral amount using contract's collateral factor
      // creationCollateralFactorBasisPoints is the max LTV in bps (5600 = 56% LTV).
      // The multiplier (required collateral value / credited value) is 10000 / bps.
      // e.g. 5600 bps → 1.786x, 6667 bps → 1.5x.
      let collateralFactorMultiplier = 1.5 // Default fallback: 150% collateral

      if (collateralFactor &&
          collateralFactor.creationCollateralFactorBasisPoints &&
          !isNaN(collateralFactor.creationCollateralFactorBasisPoints) &&
          collateralFactor.creationCollateralFactorBasisPoints > 0) {
        collateralFactorMultiplier = 10000 / collateralFactor.creationCollateralFactorBasisPoints
      }
      
      // Validate collateral factor multiplier
      if (isNaN(collateralFactorMultiplier) || collateralFactorMultiplier <= 0) {
        toast.error('Invalid collateral factor. Please try again.')
        console.error('🔴 [LOCsPage] Invalid collateral factor multiplier:', {
          collateralFactorMultiplier,
          collateralFactor,
        })
        return
      }
      
      // Get prices with proper fallback handling
      const creditedPrice = prices[selectedCreditedAsset.symbol]
      const collateralPrice = prices[selectedCollateralAsset.symbol]
      
      // Validate prices are valid numbers
      if (!creditedPrice || isNaN(creditedPrice) || creditedPrice <= 0) {
        toast.error(`Invalid price for ${selectedCreditedAsset.symbol}. Please try again.`)
        console.error('🔴 [LOCsPage] Invalid credited token price:', creditedPrice)
        return
      }
      
      if (!collateralPrice || isNaN(collateralPrice) || collateralPrice <= 0) {
        toast.error(`Invalid price for ${selectedCollateralAsset.symbol}. Please try again.`)
        console.error('🔴 [LOCsPage] Invalid collateral token price:', collateralPrice)
        return
      }
      
      const creditedValueUSD = Number(formData.creditedAmount) * creditedPrice
      const requiredCollateralUSD = creditedValueUSD * collateralFactorMultiplier
      const requiredCollateralTokens = requiredCollateralUSD / collateralPrice
      
      // Validate calculation result before converting to BigInt
      if (isNaN(requiredCollateralTokens) || !isFinite(requiredCollateralTokens)) {
        toast.error('Failed to calculate collateral amount. Please check your inputs.')
        console.error('🔴 [LOCsPage] Invalid collateral calculation:', {
          collateralFactorMultiplier,
          creditedValueUSD,
          requiredCollateralUSD,
          collateralPrice,
          requiredCollateralTokens,
          formAmount: formData.creditedAmount,
          creditedPrice,
        })
        return
      }
      
      // 🛡️ CRITICAL: Check if user has enough collateral in vault
      if (requiredCollateralUSD > totalCollateralValueUSD) {
        const shortfall = requiredCollateralUSD - totalCollateralValueUSD
        toast.error(
          `Insufficient collateral. You need $${requiredCollateralUSD.toFixed(2)} but only have $${totalCollateralValueUSD.toFixed(2)}. Please deposit an additional $${shortfall.toFixed(2)} to your vault.`
        )
        console.error('🔴 [LOCsPage] Insufficient collateral in vault:', {
          requiredCollateralUSD: `$${requiredCollateralUSD.toFixed(2)}`,
          totalCollateralValueUSD: `$${totalCollateralValueUSD.toFixed(2)}`,
          shortfall: `$${shortfall.toFixed(2)}`,
          creditedValueUSD: `$${creditedValueUSD.toFixed(2)}`,
          collateralFactorMultiplier: `${(collateralFactorMultiplier * 100).toFixed(0)}%`,
        })
        return
      }
      
      
      // Use string with limited precision to avoid float64 garbage digits (#11)
      const collateralStr = requiredCollateralTokens.toPrecision(15) // float64 has ~15 significant digits
      const collateralTokenAmount = parseTokenAmount(collateralStr, selectedCollateralAsset.decimals)


      await issueLOC(
        beneficiary,
        collateralTokenAddress,
        collateralTokenAmount,
        creditedTokenAddress,
        creditedAmount,
        expirationTimestamp
      )
    } catch (err) {
      console.error('🔴 [LOCsPage] Error issuing LOC:', err)
      toast.error(`Failed to issue LOC: ${err instanceof Error ? err.message : 'Unknown error'}`)
    }
  }

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    // Mark amount changes as "amount" source to prevent LTV oscillation (#6)
    if (field === 'creditedAmount') {
      setLtvSource('amount')
    }
    // Clear error for this field when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  // Real-time min/max validation for credited amount (prevents approval before meeting minimums)
  useEffect(() => {
    if (!formData.creditedAmount || !selectedCreditedAsset || !creditedTokenConfig) return
    if (typeof creditedTokenConfig.minPerDynamicLOC !== 'bigint' || typeof creditedTokenConfig.maxPerDynamicLOC !== 'bigint') return
    if (creditedTokenConfig.maxPerDynamicLOC === 0n) return
    
    try {
      const parsed = parseTokenAmount(formData.creditedAmount, selectedCreditedAsset.decimals)
      if (parsed < creditedTokenConfig.minPerDynamicLOC) {
        const minDisplay = formatUnits(creditedTokenConfig.minPerDynamicLOC, selectedCreditedAsset.decimals)
        setFormErrors(prev => ({ ...prev, creditedAmount: `Minimum LOC amount is ${minDisplay} ${selectedCreditedAsset.symbol}` }))
      } else if (parsed > creditedTokenConfig.maxPerDynamicLOC) {
        const maxDisplay = formatUnits(creditedTokenConfig.maxPerDynamicLOC, selectedCreditedAsset.decimals)
        setFormErrors(prev => ({ ...prev, creditedAmount: `Maximum LOC amount is ${maxDisplay} ${selectedCreditedAsset.symbol}` }))
      } else if (formErrors.creditedAmount?.includes('Minimum') || formErrors.creditedAmount?.includes('Maximum')) {
        // Clear only min/max errors (don't clear other creditedAmount errors)
        setFormErrors(prev => ({ ...prev, creditedAmount: '' }))
      }
    } catch {
      // ignore parse errors
    }
  }, [formData.creditedAmount, selectedCreditedAsset, creditedTokenConfig])

  const locsBreadcrumb = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Letters of Credit', url: '/locs' },
  ])

  if (!isConnected) {
    return (
      <>
        <SEO
          title="Letters of Credit"
          description="Issue trustless letters of credit backed by your crypto collateral. Secure payments and guarantees on the blockchain."
          canonical="/locs"
          jsonLd={locsBreadcrumb}
          keywords="letter of credit, LOC, crypto letter of credit, DeFi letter of credit, blockchain payment guarantee, WETH collateral, Anvil protocol"
        />
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="text-center max-w-md">
            <div className="flex justify-center mb-l">
              <div className="w-[96px] h-[96px]">
                <WalletConnectIllustration className="w-full h-full" />
              </div>
            </div>
            <h2 className="font-display font-black text-heading-xl uppercase text-text-primary mb-s">
              Connect Your Wallet
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8">
              Please connect your wallet to issue and manage Letters of Credit.
            </p>
            <Button size="lg" onClick={openConnectModal}>Connect Wallet</Button>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <SEO
        title="Letters of Credit"
        description="Issue trustless letters of credit backed by your crypto collateral. Secure payments and guarantees on the blockchain."
        canonical="/locs"
        jsonLd={locsBreadcrumb}
        keywords="letter of credit, LOC, crypto letter of credit, DeFi letter of credit, blockchain payment guarantee, WETH collateral, Anvil protocol, trustless LOC, decentralized letter of credit"
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* Header - Bitcoin.com React CL Style */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
            <div>
              <h1 className="font-display font-black text-heading-xl uppercase text-text-primary mb-2">
                Letters of Credit
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                Issue and manage trustless payment guarantees
              </p>
            </div>
            {viewMode === 'list' ? (
              <Button onClick={() => setViewMode('create')} size="lg" className="inline-flex items-center">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-2 inline-block">
                  <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                Issue New LOC
              </Button>
            ) : (
              <Button onClick={() => {
                setViewMode('list')
                resetForm()
              }} size="lg" variant="default" className="inline-flex items-center">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-2 inline-block">
                  <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Back to LOCs
              </Button>
            )}
          </div>

          {/* How It Works Flow */}
          <HowItWorksFlow 
            variant="compact" 
            progress={userProgress}
            showProgress={true}
            clickable={true}
            className="mb-8" 
          />

        {/* Low Collateral Warning */}
        {hasLowCollateral && (
          <InfoBox variant="warning" title="Low Collateral Balance">
            <p className="text-sm">
              You need at least <strong>${MINIMUM_COLLATERAL_USD}</strong> in collateral to issue LOCs. 
              Current balance: <strong>${totalCollateralValueUSD.toFixed(2)}</strong>
            </p>
            <p className="text-sm mt-2">
              <a href="/vaults" className="text-brand-orange hover:underline font-semibold">
                Deposit ${(MINIMUM_COLLATERAL_USD - totalCollateralValueUSD).toFixed(2)} more
              </a> to unlock LOC issuance.
            </p>
          </InfoBox>
        )}

        {/* Liquidation Risk Indicator */}
        {hasActiveLOCs && totalCollateralValueUSD > 0 && (
          <LiquidationRiskIndicator 
            collateralValueUSD={totalCollateralValueUSD}
            locValueUSD={estimatedLOCValueUSD}
            showRecommendations={true}
            title="Your Liquidation Risk"
          />
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <>
            {/* Role Tabs: As Creator / As Beneficiary */}
            <div className="flex gap-2 border-b-2 border-border-default mb-4 overflow-x-auto">
              <button
                onClick={() => { setRoleTab('creator'); setCurrentPage(1) }}
                className={`px-6 py-3 font-bold transition-colors border-b-2 whitespace-nowrap ${
                  roleTab === 'creator'
                    ? 'border-brand-orange text-brand-orange'
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                }`}
              >
                As Creator
                {locEvents.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700">
                    {locEvents.length}
                  </span>
                )}
              </button>
              <button
                onClick={() => { setRoleTab('beneficiary'); setCurrentPage(1) }}
                className={`px-6 py-3 font-bold transition-colors border-b-2 whitespace-nowrap ${
                  roleTab === 'beneficiary'
                    ? 'border-brand-orange text-brand-orange'
                    : 'border-transparent text-text-secondary hover:text-text-primary'
                }`}
              >
                As Beneficiary
                {beneficiaryLocEvents.length > 0 && (
                  <span className="ml-2 px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700">
                    {beneficiaryLocEvents.length}
                  </span>
                )}
              </button>
            </div>

            {/* Status Filter Tabs */}
            <div className="flex gap-2 border-b border-border-default mb-8 overflow-x-auto">
          <button
            onClick={() => setStatusFilter('all')}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 whitespace-nowrap ${
              statusFilter === 'all'
                ? 'border-brand-orange text-brand-orange'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            All LOCs
          </button>
          <button
            onClick={() => setStatusFilter(LOCStatus.Active)}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 whitespace-nowrap ${
              statusFilter === LOCStatus.Active
                ? 'border-green-600 text-green-600'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            Active
          </button>
          <button
            onClick={() => setStatusFilter(LOCStatus.Redeemed)}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 whitespace-nowrap ${
              statusFilter === LOCStatus.Redeemed
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            Redeemed
          </button>
          <button
            onClick={() => setStatusFilter(LOCStatus.Expired)}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 whitespace-nowrap ${
              statusFilter === LOCStatus.Expired
                ? 'border-gray-600 text-gray-600'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            Expired
          </button>
          <button
            onClick={() => setStatusFilter(LOCStatus.Cancelled)}
            className={`px-6 py-3 font-semibold transition-colors border-b-2 whitespace-nowrap ${
              statusFilter === LOCStatus.Cancelled
                ? 'border-red-600 text-red-600'
                : 'border-transparent text-text-secondary hover:text-text-primary'
            }`}
          >
            Cancelled
          </button>
        </div>

        {/* Global LOC Usage Bar — P2 task #4.9 */}
        {selectedCreditedAsset && creditedTokenConfig && 
         typeof creditedTokenConfig.globalMaxInDynamicUse === 'bigint' &&
         typeof creditedTokenConfig.globalAmountInDynamicUse === 'bigint' &&
         creditedTokenConfig.globalMaxInDynamicUse > 0n && (
          <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                Global LOC Capacity ({selectedCreditedAsset.symbol})
              </h4>
              <span className="text-sm font-mono text-gray-600 dark:text-gray-400">
                {formatUnits(creditedTokenConfig.globalAmountInDynamicUse, selectedCreditedAsset.decimals)} / {formatUnits(creditedTokenConfig.globalMaxInDynamicUse, selectedCreditedAsset.decimals)}
              </span>
            </div>
            {(() => {
              // Use parseFloat(formatUnits()) instead of Number() to avoid BigInt precision loss
              const usageBigInt = creditedTokenConfig.globalAmountInDynamicUse
              const maxBigInt = creditedTokenConfig.globalMaxInDynamicUse
              const usage = parseFloat(formatUnits(usageBigInt, selectedCreditedAsset.decimals))
              const max = parseFloat(formatUnits(maxBigInt, selectedCreditedAsset.decimals))
              const pct = max > 0 ? (usage / max) * 100 : 0
              const remainingBigInt = maxBigInt > usageBigInt ? maxBigInt - usageBigInt : 0n
              return (
                <>
                  <div className="relative h-4 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className={`absolute h-full transition-all duration-300 ${
                        pct >= 90 ? 'bg-red-500' : pct >= 75 ? 'bg-orange-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2 text-xs">
                    <span className="text-gray-600 dark:text-gray-400">
                      {pct.toFixed(1)}% used
                    </span>
                    <span className={`font-semibold ${
                      pct >= 90 ? 'text-red-600 dark:text-red-400' :
                      pct >= 75 ? 'text-orange-600 dark:text-orange-400' :
                      'text-green-600 dark:text-green-400'
                    }`}>
                      {formatUnits(remainingBigInt, selectedCreditedAsset.decimals)} {selectedCreditedAsset.symbol} available
                    </span>
                  </div>
                </>
              )
            })()}
          </div>
        )}

        {/* LOCs List with Pagination — P2 task #4.10 */}
        {isLoadingLocs ? (
          <div className="flex justify-center py-12">
            <div className="spinner h-12 w-12" />
          </div>
        ) : locIds && locIds.length > 0 ? (
          <>
            <div className="grid gap-6">
              {locIds.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE).map((locId) => (
                <LOCCard
                  key={locId.toString()}
                  locId={locId}
                  statusFilter={statusFilter}
                  locEvent={locEventMap.get(locId.toString())}
                  prices={prices}
                />
              ))}
            </div>
            
            {/* Pagination Controls */}
            {locIds.length > PAGE_SIZE && (
              <div className="mt-8 flex items-center justify-between">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Showing {((currentPage - 1) * PAGE_SIZE) + 1} to {Math.min(currentPage * PAGE_SIZE, locIds.length)} of {locIds.length} LOCs
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="default"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-4 py-2"
                  >
                    Previous
                  </Button>
                  {Array.from({ length: Math.ceil(locIds.length / PAGE_SIZE) }, (_, i) => i + 1)
                    .filter(page => {
                      // Show first, last, current, and adjacent pages
                      return page === 1 || 
                             page === Math.ceil(locIds.length / PAGE_SIZE) ||
                             Math.abs(page - currentPage) <= 1
                    })
                    .map((page, idx, arr) => (
                      <Fragment key={page}>
                        {idx > 0 && arr[idx - 1] !== page - 1 && (
                          <span className="px-3 py-2 text-gray-400">...</span>
                        )}
                        <Button
                          variant={page === currentPage ? 'primary' : 'default'}
                          onClick={() => setCurrentPage(page)}
                          className="px-4 py-2"
                        >
                          {page}
                        </Button>
                      </Fragment>
                    ))}
                  <Button
                    variant="default"
                    onClick={() => setCurrentPage(p => Math.min(Math.ceil(locIds.length / PAGE_SIZE), p + 1))}
                    disabled={currentPage === Math.ceil(locIds.length / PAGE_SIZE)}
                    className="px-4 py-2"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
          ) : (
            <Card>
              <CardContent className="text-center py-16">
                <div className="flex justify-center mb-6">
                  <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-brand-blue/20 to-brand-blue/10 flex items-center justify-center">
                    <DocumentIcon />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                  No LOCs Yet
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
                  Issue your first Letter of Credit backed by vault collateral to enable secure payments and guarantees
                </p>
                <div className="flex justify-center">
                  <Button onClick={() => setViewMode('create')} size="lg" variant="secondary" className="inline-flex items-center">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="mr-2 inline-block">
                      <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                    Issue Your First LOC
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          </>
        )}

        {/* Create View - Inline Form */}
        {viewMode === 'create' && (
          <div className="space-y-6">
            {/* Success State */}
            {isSuccess && hash && (
              <div className="bg-green-50 dark:bg-green-900/20 border-2 border-green-500 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h3 className="text-lg font-bold text-green-900 dark:text-green-100">
                        LOC Issued Successfully!
                      </h3>
                    </div>
                    <p className="text-sm text-green-800 dark:text-green-200 mb-3">
                      Your Letter of Credit has been created on the blockchain.
                    </p>
                    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 mb-4">
                      <p className="text-xs text-text-secondary mb-1">Transaction Hash:</p>
                      <a 
                        href={`https://etherscan.io/tx/${hash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-mono text-brand-orange hover:underline break-all"
                      >
                        {hash}
                      </a>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <Button onClick={() => {
                        setViewMode('list')
                        resetForm()
                      }} className="w-full sm:w-auto min-h-[48px]">
                        Back to LOCs
                      </Button>
                      <Button variant="default" onClick={() => {
                        resetForm()
                      }} className="w-full sm:w-auto min-h-[48px]">
                        Create Another LOC
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Error State */}
            {isError && error && (
              <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-500 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <h3 className="text-lg font-bold text-red-900 dark:text-red-100">
                        Failed to Issue LOC
                      </h3>
                    </div>
                    <p className="text-sm text-red-800 dark:text-red-200 mb-3">
                      {error.message || 'An error occurred while creating the LOC.'}
                    </p>
                    <Button variant="default" onClick={() => {
                      // Error state - keep form populated so user can try again
                    }}>
                      Try Again
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Form */}
            {!isSuccess && (
              <Card>
                <CardContent className="p-4 sm:p-8">
                  <div className="mb-6">
                    <h2 className="text-2xl sm:text-3xl font-display font-black uppercase text-text-primary mb-2">
                      Issue New LOC
                    </h2>
                    <p className="text-text-secondary">
                      Create a trustless payment guarantee backed by your collateral
                    </p>
                  </div>

                  <div className="space-y-8">
                    {/* Collateral Status Banner */}
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                          <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Your Collateral Account</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Account: </span>
                              <span className="font-mono font-medium text-gray-900 dark:text-white">{address ? formatAddress(address) : 'N/A'}</span>
                            </div>
                            <div>
                              <span className="text-gray-600 dark:text-gray-400">Available: </span>
                              <span className="font-bold text-brand-orange">${totalCollateralValueUSD.toFixed(2)}</span>
                              <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">(approx)</span>
                            </div>
                          </div>
                          {hasLowCollateral && (
                            <div className="mt-2 p-2 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg">
                              <div className="flex items-start gap-2">
                                <svg className="w-5 h-5 text-yellow-700 dark:text-yellow-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <p className="text-sm text-yellow-800 dark:text-yellow-400 font-medium">
                                  Minimum ${MINIMUM_COLLATERAL_USD} required. <a href="/vaults" className="underline font-semibold hover:text-yellow-900 dark:hover:text-yellow-300">Deposit more →</a>
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* LOC Explanation */}
                    <InfoBox variant="info" className="mb-6">
                      <div className="space-y-3">
                        <p className="font-semibold text-lg">📄 What is a Letter of Credit (LOC)?</p>
                        <p className="text-sm">
                          An LOC is a <strong>payment guarantee</strong> backed by your deposited collateral. 
                          You guarantee a specific amount to the beneficiary (recipient), who can 
                          redeem the LOC to receive those funds if needed.
                        </p>
                        <div className="text-sm text-gray-700 dark:text-gray-300 space-y-1">
                          <p>💼 <strong>Use cases:</strong> Trade finance, escrow services, business credit lines, supplier payment guarantees</p>
                          <p>🔒 <strong>How it works:</strong> You (issuer) → Lock collateral → Beneficiary → Can redeem funds</p>
                          <p>💎 <strong>Collateral:</strong> Only tokens you've deposited in your vault can be used. The payment guarantee amount is calculated based on your deposited collateral value and LTV ratio.</p>
                        </div>
                        <div className="text-sm font-semibold text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 p-3 rounded space-y-2">
                          <p>
                            ⚠️ <strong>Step 1 — Provide a Guarantee:</strong> You're locking collateral and issuing a Letter of Credit to your chosen lending partner. You don't receive funds at this stage — this creates the on-chain guarantee.
                          </p>
                          <p>
                            📋 <strong>Step 2 — Loan Application:</strong> Once your LOC is issued, the lending partner reviews your application and collateral. After KYC and approval, they disburse the loan to you based on the guarantee you've provided.
                          </p>
                        </div>
                      </div>
                    </InfoBox>
                    
                    {/* Available Collateral Tokens Notice */}
                    {availableCollateralTokens.length === 0 && (
                      <InfoBox variant="warning" className="mb-6">
                        <div className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          <div>
                            <p className="font-semibold text-yellow-800 dark:text-yellow-300">No Collateral Deposited</p>
                            <p className="text-sm text-yellow-700 dark:text-yellow-400 mt-1">
                              You need to deposit collateral to your vault before issuing a Letter of Credit. 
                              <a href="/vaults" className="underline font-semibold hover:text-yellow-900 dark:hover:text-yellow-300 ml-1">Go to Vaults →</a>
                            </p>
                          </div>
                        </div>
                      </InfoBox>
                    )}

                    {/* Collateral Factor InfoBox — P1 task #4.7 */}
                    {selectedCollateralAsset && selectedCreditedAsset && (
                      <InfoBox variant="info" className="mb-2">
                        <div className="space-y-2">
                          <p className="font-semibold text-sm">
                            🔐 Collateral Requirements
                            {isLoadingCollateralFactor && (
                              <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">
                                (loading from contract...)
                              </span>
                            )}
                          </p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                            <div className="p-2 bg-white dark:bg-gray-800 rounded-lg">
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Creation Factor</p>
                              <p className="font-bold text-lg text-brand-orange">
                                {effectiveCollateralFactor
                                  ? `${(effectiveCollateralFactor.creationCollateralFactorBasisPoints / 100).toFixed(0)}%`
                                  : isLoadingCollateralFactor 
                                    ? '...'
                                    : '—'}
                              </p>
                              <p className="text-xs text-gray-600 dark:text-gray-400">required to open</p>
                            </div>
                            <div className="p-2 bg-white dark:bg-gray-800 rounded-lg">
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Maintenance Factor</p>
                              <p className="font-bold text-lg text-brand-orange">
                                {effectiveCollateralFactor
                                  ? `${(effectiveCollateralFactor.collateralFactorBasisPoints / 100).toFixed(0)}%`
                                  : isLoadingCollateralFactor
                                    ? '...'
                                    : '—'}
                              </p>
                              <p className="text-xs text-gray-600 dark:text-gray-400">to avoid liquidation</p>
                            </div>
                            <div className="p-2 bg-white dark:bg-gray-800 rounded-lg">
                              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Max LTV</p>
                              <p className="font-bold text-lg text-green-600 dark:text-green-400">
                                {maxLTVPercent.toFixed(1)}%
                              </p>
                              <p className="text-xs text-gray-600 dark:text-gray-400">of collateral value</p>
                            </div>
                          </div>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            Example: To issue a $1,000 LOC you need at least $
                            {(1000 * collateralFactorMultiplier).toFixed(0)} in collateral 
                            ({(collateralFactorMultiplier * 100).toFixed(0)}% of LOC value).
                            {effectiveCollateralFactor
                              ? collateralFactor && collateralFactor.creationCollateralFactorBasisPoints > 0
                                ? ' Values sourced from Anvil Protocol contract.'
                                : ' Using verified configuration from Mainnet.'
                              : ' This token pair may not be configured yet.'}
                          </p>
                        </div>
                      </InfoBox>
                    )}

                    {/* Token Pair Not Configured Warning */}
                    {selectedCollateralAsset && selectedCreditedAsset && 
                     !isLoadingCollateralFactor && !effectiveCollateralFactor && (
                      <InfoBox variant="warning" className="mb-2">
                        <p className="font-semibold text-sm mb-1">⚠️ Token Pair Not Configured</p>
                        <p className="text-sm">
                          The {selectedCollateralAsset.symbol} → {selectedCreditedAsset.symbol} loan pair 
                          is not currently supported by the Anvil Protocol. 
                          Available pairs for {selectedCollateralAsset.symbol}: {availableCreditedTokens.map(t => t.symbol).join(', ')}
                        </p>
                      </InfoBox>
                    )}

                    {/* Form Section */}
                    <div className="space-y-6">
                      <h3 className="text-lg font-bold text-text-primary">Payment Guarantee Details</h3>

                      {/* Beneficiary Address */}
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Payment Recipient (Beneficiary) <span className="text-red-500">*</span>
                        </label>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                          Select a lending partner to apply for a loan, or enter a custom recipient address
                        </p>

                        {/* Lending Partner Selector */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                          {LENDING_PARTNERS.map((partner) => {
                            const isSelected = formData.beneficiary.toLowerCase() === partner.address.toLowerCase()
                            return (
                              <div key={partner.key} className="relative">
                                <button
                                  type="button"
                                  onClick={() => handleInputChange('beneficiary', partner.address)}
                                  disabled={isPending}
                                  className={`w-full relative flex flex-col gap-2 p-4 rounded-xl border-2 transition-all duration-200 text-left
                                    ${isSelected
                                      ? 'border-brand-orange bg-orange-50 dark:bg-orange-950/30 shadow-md ring-1 ring-brand-orange/30'
                                      : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm'
                                    }
                                    disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                  {/* Top row: icon + name + country */}
                                  <div className="flex items-center gap-3">
                                    <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center text-lg
                                      ${isSelected ? 'bg-brand-orange/10' : 'bg-gray-100 dark:bg-gray-700'}`}>
                                      {partner.flag}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-sm font-bold ${isSelected ? 'text-brand-orange' : 'text-gray-900 dark:text-white'}`}>
                                        {partner.name}
                                      </p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400">
                                        {partner.country}
                                      </p>
                                    </div>
                                    {isSelected && (
                                      <svg className="w-5 h-5 text-brand-orange flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                      </svg>
                                    )}
                                  </div>

                                  {/* Rate + LTV summary */}
                                  <div className="flex gap-4 mt-1">
                                    <div>
                                      <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium">Interest</p>
                                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">{partner.interestRate}</p>
                                    </div>
                                    <div>
                                      <p className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 font-medium">Max LTV</p>
                                      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">{partner.ltv}</p>
                                    </div>
                                  </div>
                                </button>

                                {/* Learn More link — outside the select button */}
                                <a
                                  href={`/partner-bank/${partner.slug}`}
                                  className="block mt-1.5 text-center text-xs font-medium text-brand-orange hover:text-orange-600 dark:hover:text-orange-400 transition-colors"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  Learn more about {partner.name} →
                                </a>
                              </div>
                            )
                          })}
                        </div>

                        {/* Divider */}
                        <div className="relative my-4">
                          <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-200 dark:border-gray-700"></div>
                          </div>
                          <div className="relative flex justify-center text-xs">
                            <span className="px-3 bg-gray-50 dark:bg-gray-900 text-gray-400 dark:text-gray-500 uppercase tracking-wider font-medium">
                              or enter address manually
                            </span>
                          </div>
                        </div>

                        {/* Manual Input */}
                        <div className="flex flex-col sm:flex-row gap-2">
                          <input
                            id="beneficiary"
                            type="text"
                            placeholder="0x... or ENS name"
                            value={
                              LENDING_PARTNERS.some(p => p.address.toLowerCase() === formData.beneficiary.toLowerCase())
                                ? ''
                                : formData.beneficiary
                            }
                            onChange={(e) => handleInputChange('beneficiary', e.target.value)}
                            onFocus={() => {
                              // If a bank partner was selected, clear it so user can type freely
                              if (LENDING_PARTNERS.some(p => p.address.toLowerCase() === formData.beneficiary.toLowerCase())) {
                                handleInputChange('beneficiary', '')
                              }
                            }}
                            disabled={isPending}
                            className="w-full flex-1 px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange disabled:bg-gray-100 dark:disabled:bg-gray-900 disabled:cursor-not-allowed text-sm sm:text-base"
                          />
                          <Button 
                            variant="default" 
                            onClick={handlePaste}
                            disabled={isPending}
                            className="w-full sm:w-auto sm:flex-shrink-0 px-4 min-h-[48px]"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="inline-block mr-1">
                              <path d="M8 5H6C4.89543 5 4 5.89543 4 7V19C4 20.1046 4.89543 21 6 21H16C17.1046 21 18 20.1046 18 19V18M8 5C8 3.89543 8.89543 3 10 3H12C13.1046 3 14 3.89543 14 5M8 5C8 6.10457 8.89543 7 10 7H12C13.1046 7 14 6.10457 14 5M18 18C19.1046 18 20 17.1046 20 16V8C20 6.89543 19.1046 6 18 6H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                            </svg>
                            Paste
                          </Button>
                        </div>

                        {/* Selected partner confirmation */}
                        {LENDING_PARTNERS.some(p => p.address.toLowerCase() === formData.beneficiary.toLowerCase()) && (
                          <div className="mt-2 p-2.5 rounded-lg bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900/30">
                            <p className="text-xs text-orange-700 dark:text-orange-400 font-medium">
                              ✓ Applying to <strong>{LENDING_PARTNERS.find(p => p.address.toLowerCase() === formData.beneficiary.toLowerCase())?.name}</strong> — {formatAddress(formData.beneficiary)}
                            </p>
                          </div>
                        )}
                      
                        
                        {/* ENS Resolution */}
                        {formData.beneficiary && formData.beneficiary.endsWith('.eth') && (
                          <div className="mt-2 p-3 rounded-lg bg-gray-50 dark:bg-gray-800/50">
                            {ensLoading ? (
                              <div className="flex items-center gap-2">
                                <svg className="animate-spin h-4 w-4 text-gray-600 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <p className="text-sm text-gray-600 dark:text-gray-400">Resolving ENS name...</p>
                              </div>
                            ) : ensResolvedAddress ? (
                              <div className="flex items-start gap-2">
                                <svg className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <p className="text-sm text-green-600 dark:text-green-400">
                                  Resolves to:{' '}
                                  <a 
                                    href={`https://etherscan.io/address/${ensResolvedAddress}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-mono font-semibold underline hover:text-green-700 dark:hover:text-green-300"
                                  >
                                    {formatAddress(ensResolvedAddress)}
                                  </a>
                                </p>
                              </div>
                            ) : ensError ? (
                              <div className="flex items-start gap-2">
                                <svg className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                <p className="text-sm text-red-600 dark:text-red-400">
                                  {ensError}
                                </p>
                              </div>
                            ) : null}
                          </div>
                        )}
                        
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                          The Ethereum address or ENS name that can redeem this LOC
                        </p>
                        {formErrors.beneficiary && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400 font-medium">{formErrors.beneficiary}</p>
                        )}
                      </div>

                      {/* LTV Slider */}
                      {totalCollateralValueUSD > 0 && (
                        <div className="p-4 bg-white dark:bg-gray-800 rounded-xl border-2 border-gray-200 dark:border-gray-700 shadow-sm">
                          <div className="flex items-center justify-between mb-3">
                            <label className="text-sm font-semibold text-gray-900 dark:text-white">
                              Loan-to-Value (LTV) Ratio
                            </label>
                            <div className="text-right">
                              <span className={`text-2xl font-bold ${
                                ltvRisk.level === 'safe' ? 'text-green-600 dark:text-green-400' :
                                ltvRisk.level === 'moderate' ? 'text-yellow-600 dark:text-yellow-400' :
                                ltvRisk.level === 'high' ? 'text-orange-600 dark:text-orange-400' :
                                ltvRisk.level === 'danger' ? 'text-red-600 dark:text-red-400' :
                                'text-gray-900 dark:text-white animate-pulse'
                              }`}>
                                {ltvPercentage.toFixed(1)}%
                              </span>
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                Max: {Math.floor(absoluteMaxLTV)}%
                              </div>
                            </div>
                          </div>

                          {/* Slider */}
                          <input
                            type="range"
                            min="10"
                            max={Math.floor(absoluteMaxLTV)}
                            step="1"
                            value={ltvPercentage}
                            onChange={(e) => {
                              const newLtv = parseInt(e.target.value)
                              setLtvSource('slider')
                              setLtvPercentage(newLtv)
                              // Auto-calculate LOC amount based on LTV
                              const calculatedAmount = (totalCollateralValueUSD * (newLtv / 100)) / (prices?.[selectedCreditedAsset!.symbol] || 1)
                              handleInputChange('creditedAmount', calculatedAmount.toFixed(4))
                            }}
                            disabled={isPending}
                            className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-brand-orange"
                            style={{
                              background: `linear-gradient(to right, 
                                #10b981 0%, #10b981 ${(maxLTVPercent * 0.60 / absoluteMaxLTV * 100).toFixed(1)}%, 
                                #eab308 ${(maxLTVPercent * 0.60 / absoluteMaxLTV * 100).toFixed(1)}%, #eab308 ${(maxLTVPercent * 0.75 / absoluteMaxLTV * 100).toFixed(1)}%, 
                                #f97316 ${(maxLTVPercent * 0.75 / absoluteMaxLTV * 100).toFixed(1)}%, #f97316 ${(maxLTVPercent * 0.90 / absoluteMaxLTV * 100).toFixed(1)}%,
                                #ef4444 ${(maxLTVPercent * 0.90 / absoluteMaxLTV * 100).toFixed(1)}%, #ef4444 ${(extremeZoneStart / absoluteMaxLTV * 100).toFixed(1)}%,
                                #1f2937 ${(extremeZoneStart / absoluteMaxLTV * 100).toFixed(1)}%, #1f2937 100%)`
                            }}
                          />

                          {/* Range labels */}
                          <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400 mt-1 mb-3">
                            <span>10% (Very Safe)</span>
                            <span className="font-semibold text-green-600 dark:text-green-400">{Math.floor(maxLTVPercent * 0.50)}% (Recommended)</span>
                            <span className="font-bold text-gray-900 dark:text-gray-100">{Math.floor(absoluteMaxLTV)}% (Protocol Max)</span>
                          </div>

                          {/* Risk indicator */}
                          <div className={`p-3 rounded-lg border-2 ${
                            ltvRisk.level === 'safe' ? 'bg-green-100 dark:bg-green-900/30 border-green-300 dark:border-green-700' :
                            ltvRisk.level === 'moderate' ? 'bg-yellow-100 dark:bg-yellow-900/30 border-yellow-300 dark:border-yellow-700' :
                            ltvRisk.level === 'high' ? 'bg-orange-100 dark:bg-orange-900/30 border-orange-300 dark:border-orange-700' :
                            ltvRisk.level === 'danger' ? 'bg-red-100 dark:bg-red-900/30 border-red-400 dark:border-red-600' :
                            'bg-gray-900 dark:bg-gray-950 border-gray-950 dark:border-gray-900'
                          }`}>
                            <div className="flex items-start gap-2">
                              {ltvRisk.level === 'extreme' ? (
                                <svg className="w-6 h-6 text-white dark:text-white flex-shrink-0 mt-0.5 animate-pulse" fill="currentColor" viewBox="0 0 24 24">
                                  <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
                                </svg>
                              ) : ltvRisk.level === 'danger' || ltvRisk.level === 'high' ? (
                                <svg className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                              ) : ltvRisk.level === 'moderate' ? (
                                <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              )}
                              <div className="flex-1">
                                <p className={`text-sm font-semibold ${
                                  ltvRisk.level === 'safe' ? 'text-green-800 dark:text-green-200' :
                                  ltvRisk.level === 'moderate' ? 'text-yellow-800 dark:text-yellow-200' :
                                  ltvRisk.level === 'high' ? 'text-orange-800 dark:text-orange-200' :
                                  ltvRisk.level === 'danger' ? 'text-red-800 dark:text-red-200' :
                                  'text-white dark:text-white font-bold'
                                }`}>
                                  {ltvRisk.message}
                                </p>
                                <p className={`text-xs mt-1 ${
                                  ltvRisk.level === 'extreme' ? 'text-gray-300 dark:text-gray-300' : 'text-gray-700 dark:text-gray-300'
                                }`}>
                                  You can borrow up to <strong>${maxLOCValue.toFixed(2)}</strong> based on your ${totalCollateralValueUSD.toFixed(2)} collateral.
                                </p>
                                {ltvRisk.level === 'extreme' && (
                                  <p className="text-xs text-red-400 dark:text-red-400 mt-2 font-bold animate-pulse">
                                    ⚠️ This is NOT recommended. Even a 1% price drop will trigger liquidation!
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* LOC Amount - Two column on desktop */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
                            Payment Guarantee Amount <span className="text-red-500">*</span>
                          </label>
                          {selectedCreditedAsset?.symbol === 'USDC' && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 text-xs font-semibold rounded-md">
                              <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Stablecoin Recommended
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div>
                            <TokenSelector
                              value={selectedCollateralAsset}
                              onChange={(token) => setSelectedCollateralAsset(token as Token)}
                              tokens={availableCollateralTokens}
                              showBalance={true}
                              label="Collateral Token (from vault)"
                              disabled={isPending || availableCollateralTokens.length === 0}
                              vaultBalances={collateralData}
                            />
                            {availableCollateralTokens.length === 0 && (
                              <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                                ⚠️ No tokens deposited. <a href="/vaults" className="underline font-semibold">Deposit to vault first →</a>
                              </p>
                            )}
                          </div>
                          <div>
                            <TokenSelector
                              value={selectedCreditedAsset}
                              onChange={(token) => setSelectedCreditedAsset(token as Token)}
                              tokens={availableCreditedTokens}
                              showBalance={false}
                              label="Loan Currency"
                              disabled={isPending || availableCreditedTokens.length === 0}
                            />
                            {availableCreditedTokens.length === 0 && selectedCollateralAsset && (
                              <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                                ⚠️ No loan currencies available for {selectedCollateralAsset.symbol}
                              </p>
                            )}
                          </div>
                        </div>
                        
                        <div className="mt-4">
                          <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Loan Amount ({selectedCreditedAsset?.symbol || 'Token'})
                          </label>
                          <input
                            id="amount"
                            type="number"
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                            value={formData.creditedAmount}
                            onChange={(e) => handleInputChange('creditedAmount', e.target.value)}
                            disabled={isPending || !effectiveCollateralFactor}
                            className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange disabled:bg-gray-100 dark:disabled:bg-gray-900 disabled:cursor-not-allowed"
                          />
                          {formData.creditedAmount && selectedCreditedAsset && prices && parseFloat(formData.creditedAmount) > 0 && (
                            <p className="mt-2 text-sm font-semibold text-brand-orange">
                              ≈ ${(parseFloat(formData.creditedAmount) * (prices[selectedCreditedAsset.symbol] || 0)).toFixed(2)} USD
                            </p>
                          )}
                          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                            The amount the beneficiary will receive when redeemed
                          </p>
                        </div>
                        
                        {/* Min/Max LOC limits from contract — P1 task #4.8 */}
                        {creditedTokenConfig && 
                         selectedCreditedAsset && 
                         typeof selectedCreditedAsset.decimals === 'number' &&
                         typeof creditedTokenConfig.minPerDynamicLOC === 'bigint' && 
                         typeof creditedTokenConfig.maxPerDynamicLOC === 'bigint' && 
                         creditedTokenConfig.maxPerDynamicLOC > 0n && (() => {
                           const minAmt = parseFloat(formatUnits(creditedTokenConfig.minPerDynamicLOC, selectedCreditedAsset.decimals))
                           const maxAmt = parseFloat(formatUnits(creditedTokenConfig.maxPerDynamicLOC, selectedCreditedAsset.decimals))
                           const currentAmt = parseFloat(formData.creditedAmount || '0')
                           const belowMin = currentAmt > 0 && currentAmt < minAmt
                           const aboveMax = currentAmt > 0 && currentAmt > maxAmt
                           return (
                             <>
                               <div className={`mt-2 p-2 rounded-lg border ${belowMin || aboveMax ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-700' : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'}`}>
                                 <p className={`text-xs font-semibold mb-1 ${belowMin || aboveMax ? 'text-red-700 dark:text-red-300' : 'text-blue-700 dark:text-blue-300'}`}>
                                   Protocol Limits ({selectedCreditedAsset.symbol}):
                                 </p>
                                 <div className="flex gap-4 text-xs">
                                   <span className={`font-medium ${belowMin ? 'text-red-700 dark:text-red-300 font-bold' : 'text-gray-700 dark:text-gray-300'}`}>
                                     Min: {minAmt.toLocaleString()} {selectedCreditedAsset.symbol}
                                     {belowMin && ' ← too low!'}
                                   </span>
                                   <span className={`font-medium ${aboveMax ? 'text-red-700 dark:text-red-300 font-bold' : 'text-gray-700 dark:text-gray-300'}`}>
                                     Max: {maxAmt.toLocaleString()} {selectedCreditedAsset.symbol}
                                     {aboveMax && ' ← too high!'}
                                   </span>
                                 </div>
                               </div>
                               {belowMin && (
                                 <p className="mt-1 text-sm text-red-600 dark:text-red-400 font-medium">
                                   ⛔ Amount is below the minimum of {minAmt.toLocaleString()} {selectedCreditedAsset.symbol}
                                 </p>
                               )}
                               {aboveMax && (
                                 <p className="mt-1 text-sm text-red-600 dark:text-red-400 font-medium">
                                   ⛔ Amount exceeds the maximum of {maxAmt.toLocaleString()} {selectedCreditedAsset.symbol}
                                 </p>
                               )}
                             </>
                           )
                         })()}
                        
                        {/* Warning if token isn't configured as a credited token on Anvil */}
                        {creditedTokenConfig && 
                         selectedCreditedAsset && 
                         typeof creditedTokenConfig.maxPerDynamicLOC === 'bigint' && 
                         creditedTokenConfig.maxPerDynamicLOC === 0n && (
                          <div className="mt-2 p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-200 dark:border-yellow-800">
                            <p className="text-xs text-yellow-700 dark:text-yellow-300">
                              <span className="font-semibold">⚠️ Note:</span>{' '}
                              {selectedCreditedAsset.symbol} may not be configured as a credited token on Anvil. 
                              Consider using USDC for the LOC value instead.
                            </p>
                          </div>
                        )}
                        
                        {/* Warning if LOC amount exceeds safe LTV or requires more collateral than available */}
                        {formData.creditedAmount && selectedCreditedAsset && selectedCollateralAsset && prices && totalCollateralValueUSD > 0 && (() => {
                          const locValueUSD = parseFloat(formData.creditedAmount) * (prices[selectedCreditedAsset.symbol] || 0)
                          const collateralFactorMultiplier = (collateralFactor?.creationCollateralFactorBasisPoints &&
                                                             !isNaN(collateralFactor.creationCollateralFactorBasisPoints) &&
                                                             collateralFactor.creationCollateralFactorBasisPoints > 0)
                            ? 10000 / collateralFactor.creationCollateralFactorBasisPoints
                            : 1.5
                          const requiredCollateralForLOC = locValueUSD * collateralFactorMultiplier
                          
                          // CRITICAL: Check if insufficient collateral
                          if (requiredCollateralForLOC > totalCollateralValueUSD) {
                            const shortfall = requiredCollateralForLOC - totalCollateralValueUSD
                            return (
                              <div className="mt-2 p-3 bg-red-100 dark:bg-red-900/30 rounded-lg border-2 border-red-400 dark:border-red-600">
                                <div className="flex items-start gap-2">
                                  <svg className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  </svg>
                                  <div>
                                    <p className="text-sm font-bold text-red-900 dark:text-red-100">
                                      ⛔ Insufficient Collateral
                                    </p>
                                    <p className="text-sm text-red-800 dark:text-red-200 mt-1">
                                      Required: <strong>${requiredCollateralForLOC.toFixed(2)}</strong> ({(collateralFactorMultiplier * 100).toFixed(0)}% collateralization)
                                    </p>
                                    <p className="text-sm text-red-800 dark:text-red-200">
                                      Available: <strong>${totalCollateralValueUSD.toFixed(2)}</strong>
                                    </p>
                                    <p className="text-sm text-red-800 dark:text-red-200 mt-1 font-semibold">
                                      Shortfall: <span className="text-red-900 dark:text-red-100">${shortfall.toFixed(2)}</span>
                                    </p>
                                    <p className="text-xs text-red-700 dark:text-red-300 mt-2">
                                      <a href="/vaults" className="underline font-bold hover:text-red-900 dark:hover:text-red-100">
                                        Deposit ${shortfall.toFixed(2)} more to your vault →
                                      </a>
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )
                          }
                          
                          const actualLTV = (locValueUSD / totalCollateralValueUSD) * 100
                          const maxLTV = (1 / collateralFactorMultiplier) * 100
                          const extremeZone = maxLTV * 0.98
                          const dangerZone = maxLTV * 0.90
                          const highZone = maxLTV * 0.75
                          const moderateZone = maxLTV * 0.60
                          
                          // Extreme zone (98-100% of max)
                          if (actualLTV >= extremeZone) {
                            return (
                              <div className="mt-2 p-3 bg-gray-900 dark:bg-gray-950 rounded-lg border-2 border-gray-950 dark:border-gray-900">
                                <div className="flex items-start gap-2">
                                  <svg className="w-6 h-6 text-white flex-shrink-0 mt-0.5 animate-pulse" fill="currentColor" viewBox="0 0 24 24">
                                    <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
                                  </svg>
                                  <div>
                                    <p className="text-sm font-bold text-white">
                                      ⛔ EXTREME RISK: {actualLTV.toFixed(1)}% LTV
                                    </p>
                                    <p className="text-xs text-gray-300 mt-1">
                                      You are at {((actualLTV / maxLTV) * 100).toFixed(0)}% of protocol maximum ({maxLTV.toFixed(1)}%)!
                                    </p>
                                    <p className="text-xs text-red-400 mt-2 font-bold animate-pulse">
                                      ⚠️ This will liquidate on ANY price movement! Reduce amount NOW!
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )
                          }
                          // Danger zone (90-98% of max)
                          else if (actualLTV >= dangerZone) {
                            return (
                              <div className="mt-2 p-3 bg-red-100 dark:bg-red-900/30 rounded-lg border-2 border-red-400 dark:border-red-600">
                                <div className="flex items-start gap-2">
                                  <svg className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  </svg>
                                  <div>
                                    <p className="text-sm font-bold text-red-900 dark:text-red-100">
                                      Critical Risk: {actualLTV.toFixed(1)}% LTV
                                    </p>
                                    <p className="text-xs text-red-800 dark:text-red-200 mt-1">
                                      Very high liquidation risk! A small price drop will trigger liquidation.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )
                          }
                          // High risk zone (75-90% of max)
                          else if (actualLTV >= highZone) {
                            return (
                              <div className="mt-2 p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg border border-orange-300 dark:border-orange-800">
                                <div className="flex items-start gap-2">
                                  <svg className="w-5 h-5 text-orange-600 dark:text-orange-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                  </svg>
                                  <div>
                                    <p className="text-sm font-semibold text-orange-800 dark:text-orange-200">
                                      High Risk: {actualLTV.toFixed(1)}% LTV
                                    </p>
                                    <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">
                                      Monitor collateral closely. Consider reducing the LOC amount.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )
                          }
                          // Moderate risk zone (60-75% of max)
                          else if (actualLTV >= moderateZone) {
                            return (
                              <div className="mt-2 p-3 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg border border-yellow-300 dark:border-yellow-800">
                                <div className="flex items-start gap-2">
                                  <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                  <div>
                                    <p className="text-sm font-semibold text-yellow-800 dark:text-yellow-200">
                                      Moderate Risk: {actualLTV.toFixed(1)}% LTV
                                    </p>
                                    <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-1">
                                      Keep an eye on your collateral value to maintain healthy margin.
                                    </p>
                                  </div>
                                </div>
                              </div>
                            )
                          }
                          return null
                        })()}
                        
                        {formErrors.creditedAmount && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400 font-medium">{formErrors.creditedAmount}</p>
                        )}
                      </div>

                      {/* Expiration Date & Time */}
                      <div>
                        <label htmlFor="expiryDate" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Expiration Date & Time <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="expiryDate"
                          type="datetime-local"
                          value={formData.expiryDate}
                          onChange={(e) => handleInputChange('expiryDate', e.target.value)}
                          disabled={isPending}
                          min={new Date().toISOString().slice(0, 16)}
                          className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange disabled:bg-gray-100 dark:disabled:bg-gray-900 disabled:cursor-not-allowed"
                        />
                        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                          When this LOC expires and can no longer be redeemed (includes specific time)
                        </p>
                        {formErrors.expiryDate && (
                          <p className="mt-1 text-sm text-red-600 dark:text-red-400 font-medium">{formErrors.expiryDate}</p>
                        )}
                      </div>
                    </div>

                    {/* Optional Fields Section */}
                    <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
                      <h3 className="text-lg font-bold text-text-primary mb-4">Additional Information <span className="text-sm font-normal text-gray-500">(Optional)</span></h3>
                      
                      <div className="space-y-4">
                        {/* Memo */}
                        <div>
                          <label htmlFor="memo" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                            Memo
                          </label>
                          <textarea
                            id="memo"
                            className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-brand-orange focus:border-brand-orange bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 resize-none disabled:bg-gray-100 dark:disabled:bg-gray-900 disabled:cursor-not-allowed"
                            rows={3}
                            placeholder="Add notes or reference information..."
                            value={formData.memo}
                            onChange={(e) => handleInputChange('memo', e.target.value)}
                            disabled={isPending}
                          />
                          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            Add optional notes or reference information for this LOC
                          </p>
                        </div>

                      </div>
                    </div>

                    {/* LOC Summary */}
                    {formData.creditedAmount && selectedCreditedAsset && formData.expiryDate && prices && parseFloat(formData.creditedAmount) > 0 && (
                      <div className="p-6 bg-gradient-to-br from-brand-orange/5 to-brand-orange/10 dark:from-brand-orange/10 dark:to-brand-orange/5 rounded-2xl border-2 border-brand-orange/20">
                        <div className="flex items-center gap-2 mb-4">
                          <svg className="w-5 h-5 text-brand-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                          </svg>
                          <h4 className="text-base font-bold text-gray-900 dark:text-white">
                            LOC Summary
                          </h4>
                        </div>
                        <div className="space-y-3">
                          <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Amount:</span>
                            <span className="text-lg font-bold text-gray-900 dark:text-white">
                              {formData.creditedAmount} {selectedCreditedAsset.symbol}
                            </span>
                          </div>
                          <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Value (USD):</span>
                            <span className="text-xl font-bold text-brand-orange">
                              ${(parseFloat(formData.creditedAmount) * (prices[selectedCreditedAsset.symbol] || 0)).toFixed(2)}
                            </span>
                          </div>
                          <div className="flex justify-between items-center py-2">
                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Expires:</span>
                            <span className="text-sm font-semibold text-gray-900 dark:text-white">
                              {new Date(formData.expiryDate).toLocaleString('en-US', { 
                                year: 'numeric', 
                                month: 'long', 
                                day: 'numeric',
                                hour: 'numeric',
                                minute: '2-digit',
                                hour12: true
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex flex-col-reverse sm:flex-row gap-3 pt-8 border-t border-gray-200 dark:border-gray-700 mt-8">
                      <Button 
                        variant="default" 
                        onClick={() => {
                          setViewMode('list')
                          resetForm()
                        }}
                        disabled={isPending}
                        className="w-full sm:w-auto sm:flex-1 px-6 py-3"
                      >
                        Cancel
                      </Button>
                      {/* Approval Button (shown if collateral not approved) */}
                      {!hasCollateralizableAllowance && !isLoadingAllowance && selectedCollateralAsset && (
                        <Button 
                          onClick={async () => {
                            if (!selectedCollateralAsset?.address) {
                              toast.error('Please select a collateral token')
                              return
                            }
                            if (!hasSelectedCollateralDeposit) {
                              toast.error(`You need to deposit ${selectedCollateralAsset.symbol} to your vault first`)
                              return
                            }
                            await approveUnlimited(selectedCollateralAsset.address, locContractAddress)
                          }}
                          disabled={isApprovingCollateral || !hasAccount || hasLowCollateral || !hasSelectedCollateralDeposit || !!formErrors.creditedAmount || !formData.creditedAmount}
                          className="w-full sm:flex-[2] px-8 py-3 text-base font-semibold bg-blue-600 hover:bg-blue-700"
                        >
                          {isApprovingCollateral ? (
                            <>
                              <svg className="animate-spin h-5 w-5 mr-2 inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Approving Collateral...
                            </>
                          ) : (
                            <>
                              <svg className="w-5 h-5 mr-2 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                              </svg>
                              Step 1: Approve Collateral Use
                            </>
                          )}
                        </Button>
                      )}
                      
                      {/* Issue LOC Button (shown if collateral approved) */}
                      {(hasCollateralizableAllowance || isLoadingAllowance) && (
                        <Button 
                          onClick={handleIssueLOC}
                          disabled={
                            isPending || 
                            isLoadingAllowance ||
                            isVerifyingApproval ||
                            !hasAccount ||
                            !hasCollateralizableAllowance ||
                            !formData.beneficiary || 
                            !formData.creditedAmount || 
                            !formData.expiryDate ||
                            !!formErrors.beneficiary ||
                            !!formErrors.creditedAmount ||
                            !!formErrors.expiryDate ||
                            hasLowCollateral ||
                            hasInsufficientCollateral
                          }
                          className="w-full sm:flex-[2] px-8 py-3 text-base font-semibold"
                        >
                          {isPending ? (
                            <>
                              <svg className="animate-spin h-5 w-5 mr-2 inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Issuing Payment Guarantee...
                            </>
                          ) : isLoadingAllowance ? (
                            'Checking Approval...'
                          ) : (
                            <>
                              <svg className="w-5 h-5 mr-2 inline-block" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              {hasCollateralizableAllowance ? 'Issue Payment Guarantee' : 'Approve First'}
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
        </div>
      </div>
    </>
  )
}

function LOCCard({
  locId,
  statusFilter,
  locEvent,
  prices,
}: {
  locId: bigint
  statusFilter: 'all' | LOCStatus
  locEvent?: LOCEvent
  prices?: Record<string, number>
}) {
  const { loc, isLoading, refetch } = useLOCDetails(locId)
  const { redeemLOC, isPending: isRedeeming, isSuccess: redeemSuccess } = useRedeemLOC()
  const { extendLOC, isPending: isExtending, isSuccess: extendSuccess } = useExtendLOC()
  const { cancelLOC, isPending: isCancelling, isSuccess: cancelSuccess } = useCancelLOC()
  const { modifyCollateral, isPending: isModifyingCollateral, isSuccess: modifySuccess } = useModifyLOCCollateral()
  const { address } = useAccount()

  // Health indicator
  const health = useSingleLOCHealth(locEvent, prices)
  
  // Determine credited token decimals (#8 — was hardcoded to 6)
  const creditedToken = loc ? Object.values(SUPPORTED_TOKENS).find(
    t => t.address.toLowerCase() === loc.creditedAsset?.toLowerCase()
  ) : undefined
  const creditedDecimals = creditedToken?.decimals ?? 6

  const [showRedeemModal, setShowRedeemModal] = useState(false)
  const [recipientAddress, setRecipientAddress] = useState('')
  const [recipientError, setRecipientError] = useState('')
  
  // Extension modal state — P2 task #4.4
  const [showExtendModal, setShowExtendModal] = useState(false)
  const [newExpiryDate, setNewExpiryDate] = useState('')
  const [extendError, setExtendError] = useState('')

  // Cancel modal state — P0 task #3a
  const [showCancelModal, setShowCancelModal] = useState(false)

  // Add Collateral modal state — P0 task #3b
  const [showAddCollateralModal, setShowAddCollateralModal] = useState(false)
  const [addCollateralAmount, setAddCollateralAmount] = useState('')
  const [addCollateralError, setAddCollateralError] = useState('')

  // Remove Collateral modal state — MAP-19
  const [showRemoveCollateralModal, setShowRemoveCollateralModal] = useState(false)
  const [removeCollateralAmount, setRemoveCollateralAmount] = useState('')
  const [removeCollateralError, setRemoveCollateralError] = useState('')

  // Handle successful redemption
  useEffect(() => {
    if (redeemSuccess) {
      setShowRedeemModal(false)
      setRecipientAddress('')
      toast.success('LOC redeemed successfully!')
      refetch()
    }
  }, [redeemSuccess, refetch])
  
  // Handle successful extension
  useEffect(() => {
    if (extendSuccess) {
      setShowExtendModal(false)
      setNewExpiryDate('')
      toast.success('LOC expiry extended successfully!')
      refetch()
    }
  }, [extendSuccess, refetch])

  // Handle successful cancellation
  useEffect(() => {
    if (cancelSuccess) {
      setShowCancelModal(false)
      toast.success('LOC cancelled successfully!')
      refetch()
    }
  }, [cancelSuccess, refetch])

  // Handle successful collateral modification (add only — guarded to prevent double-toast with remove)
  useEffect(() => {
    if (modifySuccess && showAddCollateralModal) {
      setShowAddCollateralModal(false)
      setAddCollateralAmount('')
      toast.success('Collateral added successfully!')
      refetch()
    }
  }, [modifySuccess, showAddCollateralModal, refetch])

  const handleRedeem = async () => {
    // Validate caller is beneficiary
    if (!address) {
      toast.error('Please connect your wallet')
      return
    }
    if (!loc || loc.status !== LOCStatus.Active) {
      toast.error('This LOC is not active and cannot be redeemed')
      return
    }
    if (address.toLowerCase() !== loc.beneficiary.toLowerCase()) {
      toast.error('Only the beneficiary can redeem this LOC')
      return
    }
    // Validate recipient address
    if (!recipientAddress || !isAddress(recipientAddress)) {
      setRecipientError('Please enter a valid Ethereum address')
      return
    }

    setRecipientError('')
    await redeemLOC(locId, recipientAddress as `0x${string}`)
  }
  
  const handleExtend = async () => {
    // Validate caller is creator
    if (!address) {
      toast.error('Please connect your wallet')
      return
    }
    if (!loc || loc.status !== LOCStatus.Active) {
      toast.error('This LOC is not active and cannot be extended')
      return
    }
    if (address.toLowerCase() !== loc.creator.toLowerCase()) {
      toast.error('Only the LOC creator can extend this LOC')
      return
    }
    // Validate new expiry date
    if (!newExpiryDate) {
      setExtendError('Please select a new expiry date')
      return
    }

    const newExpiry = new Date(newExpiryDate)
    const currentExpiry = new Date(Number(loc!.expiry) * 1000)
    const now = new Date()
    
    if (newExpiry <= currentExpiry) {
      setExtendError('New expiry must be later than current expiry')
      return
    }
    
    if (newExpiry <= now) {
      setExtendError('New expiry must be in the future')
      return
    }
    
    setExtendError('')
    const newExpirationTimestamp = Math.floor(newExpiry.getTime() / 1000)
    await extendLOC(locId, newExpirationTimestamp)
  }

  const handleCancel = async () => {
    if (!address) {
      toast.error('Please connect your wallet')
      return
    }
    if (!loc || loc.status !== LOCStatus.Active) {
      toast.error('This LOC is not active and cannot be cancelled')
      return
    }
    if (address.toLowerCase() !== loc.creator.toLowerCase()) {
      toast.error('Only the LOC creator can cancel this LOC')
      return
    }
    await cancelLOC(locId)
  }

  const handleAddCollateral = async () => {
    if (!address) {
      toast.error('Please connect your wallet')
      return
    }
    if (!loc || loc.status !== LOCStatus.Active) {
      toast.error('This LOC is not active')
      return
    }
    if (address.toLowerCase() !== loc.creator.toLowerCase()) {
      toast.error('Only the LOC creator can modify collateral')
      return
    }
    if (!addCollateralAmount || isNaN(Number(addCollateralAmount)) || Number(addCollateralAmount) <= 0) {
      setAddCollateralError('Please enter a valid positive amount')
      return
    }
    setAddCollateralError('')
    // Find collateral token decimals from locEvent or default to 18
    const collateralDecimals = locEvent
      ? Object.values(SUPPORTED_TOKENS).find(
          t => t.address.toLowerCase() === locEvent.collateralTokenAddress.toLowerCase()
        )?.decimals ?? 18
      : 18
    const amountBigInt = parseTokenAmount(addCollateralAmount, collateralDecimals)
    await modifyCollateral(locId, amountBigInt)
  }

  const handleRemoveCollateral = async () => {
    if (!address) {
      toast.error('Please connect your wallet')
      return
    }
    if (!loc || loc.status !== LOCStatus.Active) {
      toast.error('This LOC is not active')
      return
    }
    if (address.toLowerCase() !== loc.creator.toLowerCase()) {
      toast.error('Only the LOC creator can modify collateral')
      return
    }
    if (!removeCollateralAmount || isNaN(Number(removeCollateralAmount)) || Number(removeCollateralAmount) <= 0) {
      setRemoveCollateralError('Please enter a valid positive amount')
      return
    }
    // LTV safety check
    if (health && locEvent && prices) {
      const collateralToken = Object.values(SUPPORTED_TOKENS).find(
        t => t.address.toLowerCase() === locEvent.collateralTokenAddress.toLowerCase()
      )
      const collateralPrice = collateralToken ? (prices[collateralToken.symbol] || 0) : 0
      const removalValueUSD = Number(removeCollateralAmount) * collateralPrice
      const newCollateralUSD = health.collateralUSD - removalValueUSD
      const newRatio = health.creditedUSD > 0 ? newCollateralUSD / health.creditedUSD : 0
      if (newRatio < health.maintenanceFactor) {
        setRemoveCollateralError(`Removing this amount would drop collateral ratio to ${(newRatio * 100).toFixed(0)}%, below the ${(health.maintenanceFactor * 100).toFixed(0)}% maintenance requirement. Risk of liquidation!`)
        return
      }
    }
    setRemoveCollateralError('')
    const collateralDecimals = locEvent
      ? Object.values(SUPPORTED_TOKENS).find(
          t => t.address.toLowerCase() === locEvent.collateralTokenAddress.toLowerCase()
        )?.decimals ?? 18
      : 18
    const amountBigInt = parseTokenAmount(removeCollateralAmount, collateralDecimals)
    // Negative amount = removal
    await modifyCollateral(locId, -amountBigInt)
  }

  // Handle successful collateral removal
  useEffect(() => {
    if (modifySuccess && showRemoveCollateralModal) {
      setShowRemoveCollateralModal(false)
      setRemoveCollateralAmount('')
      toast.success('Collateral removed successfully!')
      refetch()
    }
  }, [modifySuccess, showRemoveCollateralModal, refetch])

  if (isLoading || !loc) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex justify-center">
            <div className="spinner h-8 w-8" />
          </div>
        </CardContent>
      </Card>
    )
  }

  // Filter LOCs based on status
  if (statusFilter !== 'all' && loc.status !== statusFilter) {
    return null
  }

  // Check if current user is the beneficiary
  const isBeneficiary = address && address.toLowerCase() === loc.beneficiary.toLowerCase()

  const statusColors = {
    [LOCStatus.Active]: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-800',
    [LOCStatus.Redeemed]: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-200 dark:border-blue-800',
    [LOCStatus.Expired]: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400 border border-gray-300 dark:border-gray-700',
    [LOCStatus.Cancelled]: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800',
  }

  const statusLabels = {
    [LOCStatus.Active]: 'Active',
    [LOCStatus.Redeemed]: 'Redeemed',
    [LOCStatus.Expired]: 'Expired',
    [LOCStatus.Cancelled]: 'Cancelled',
  }

  return (
    <>
      <Card hover>
      <CardContent>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-3 sm:gap-4 flex-1 min-w-0">
            {/* Icon Badge */}
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl bg-gradient-to-br from-brand-blue/20 to-brand-blue/10 flex items-center justify-center flex-shrink-0">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-brand-blue sm:w-8 sm:h-8">
                <path d="M14 2H6C4.89543 2 4 2.89543 4 4V20C4 21.1046 4.89543 22 6 22H18C19.1046 22 20 21.1046 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8 13H16M8 17H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                <div className="text-lg sm:text-2xl font-bold text-gray-900 dark:text-white">
                  LOC #{loc.id.toString()}
                </div>
                <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${statusColors[loc.status]}`}>
                  {statusLabels[loc.status]}
                </span>
                {/* Health Indicator — 4b */}
                {health && loc.status === LOCStatus.Active && (
                  <span className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
                    health.color === 'green' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-700' :
                    health.color === 'yellow' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-200 dark:border-yellow-700' :
                    health.color === 'red' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-700' :
                    'bg-gray-100 text-gray-600 border-gray-200'
                  }`} title={`Collateral ratio: ${(health.collateralRatio * 100).toFixed(0)}% (min ${(health.maintenanceFactor * 100).toFixed(0)}%)`}>
                    {health.color === 'green' ? '🟢' : health.color === 'yellow' ? '🟡' : '🔴'} {health.label}
                  </span>
                )}
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Beneficiary</div>
                  <div className="font-medium text-gray-900 dark:text-white font-mono text-sm">
                    {formatAddress(loc.beneficiary)}
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Credited Amount</div>
                  <div className="font-bold text-gray-900 dark:text-white font-mono text-sm">
                    {formatTokenAmount(loc.creditedAmount, creditedDecimals)} {loc.creditedAssetSymbol}
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Vault ID</div>
                  <div className="font-medium text-gray-900 dark:text-white font-mono text-sm">
                    #{loc.vaultId.toString()}
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3">
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-1 uppercase tracking-wide">Expiry</div>
                  <div className="font-medium text-gray-900 dark:text-white text-sm">
                    {formatDate(Number(loc.expiry))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2 sm:ml-4 sm:flex-shrink-0">
            {loc.status === LOCStatus.Active && (
              <>
                {/* Only creator can extend, cancel, add collateral */}
                {address && address.toLowerCase() === loc.creator.toLowerCase() && (
                  <>
                    <Button 
                      size="sm" 
                      variant="default"
                      onClick={() => setShowExtendModal(true)}
                      className="flex-1 sm:flex-none min-h-[40px]"
                    >
                      Extend
                    </Button>
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => setShowAddCollateralModal(true)}
                      className="flex-1 sm:flex-none min-h-[40px]"
                    >
                      + Collateral
                    </Button>
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => setShowRemoveCollateralModal(true)}
                      className="flex-1 sm:flex-none min-h-[40px]"
                    >
                      − Collateral
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setShowCancelModal(true)}
                      className="flex-1 sm:flex-none min-h-[40px] text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                    >
                      Cancel
                    </Button>
                  </>
                )}
                {isBeneficiary && (
                  <Button 
                    size="sm" 
                    variant="secondary"
                    onClick={() => setShowRedeemModal(true)}
                    className="flex-1 sm:flex-none min-h-[40px]"
                  >
                    Redeem
                  </Button>
                )}
              </>
            )}
            <Button size="sm" variant="ghost" className="flex-1 sm:flex-none min-h-[40px]">Details</Button>
          </div>
        </div>
      </CardContent>
    </Card>

    {/* Redeem LOC Modal */}
    <Transition appear show={showRedeemModal} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setShowRedeemModal(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full sm:max-w-md transform overflow-hidden rounded-t-2xl sm:rounded-2xl bg-white dark:bg-gray-800 p-4 sm:p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-xl sm:text-2xl font-bold leading-6 text-gray-900 dark:text-white mb-4"
                >
                  Redeem LOC #{locId.toString()}
                </Dialog.Title>

                <div className="space-y-4">
                  <InfoBox variant="info" title="Redeem Letter of Credit">
                    <p className="text-sm">
                      You are redeeming <strong>{formatTokenAmount(loc.creditedAmount, creditedDecimals)} {loc.creditedAssetSymbol}</strong> from this LOC.
                      Specify the recipient address for the credited funds.
                    </p>
                  </InfoBox>

                  <div>
                    <Input
                      label="Recipient Address"
                      type="text"
                      placeholder="0x..."
                      value={recipientAddress}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        setRecipientAddress(e.target.value)
                        setRecipientError('')
                      }}
                      error={!!recipientError}
                      helperText={recipientError}
                    />
                  </div>

                  <div className="p-4 bg-shades-extra-light rounded-lg">
                    <p className="text-sm text-text-secondary mb-2">
                      <strong>LOC Details:</strong>
                    </p>
                    <ul className="text-xs text-text-secondary space-y-1">
                      <li>• Beneficiary: {formatAddress(loc.beneficiary)}</li>
                      <li>• Amount: {formatTokenAmount(loc.creditedAmount, creditedDecimals)} {loc.creditedAssetSymbol}</li>
                      <li>• Expiry: {formatDate(Number(loc.expiry))}</li>
                    </ul>
                  </div>

                  <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
                    <Button
                      variant="ghost"
                      onClick={() => setShowRedeemModal(false)}
                      disabled={isRedeeming}
                      className="w-full sm:w-auto min-h-[48px]"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleRedeem}
                      disabled={isRedeeming || !recipientAddress}
                      className="w-full sm:w-auto min-h-[48px]"
                    >
                      {isRedeeming ? 'Redeeming...' : 'Redeem LOC'}
                    </Button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>

    {/* Extend LOC Modal — P2 task #4.4 */}
    <Transition appear show={showExtendModal} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setShowExtendModal(false)}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full sm:max-w-md transform overflow-hidden rounded-t-2xl sm:rounded-2xl bg-white dark:bg-gray-800 p-4 sm:p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-xl sm:text-2xl font-bold leading-6 text-gray-900 dark:text-white mb-4"
                >
                  Extend LOC #{locId.toString()}
                </Dialog.Title>

                <div className="space-y-4">
                  <InfoBox variant="info" title="Extend Expiration Date">
                    <p className="text-sm">
                      Current expiry: <strong>{formatDate(Number(loc.expiry))}</strong>
                    </p>
                    <p className="text-sm mt-2">
                      Choose a new expiration date. It must be later than the current expiry.
                    </p>
                  </InfoBox>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      New Expiration Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      value={newExpiryDate}
                      onChange={(e) => {
                        setNewExpiryDate(e.target.value)
                        setExtendError('')
                      }}
                      min={new Date(Number(loc.expiry) * 1000 + 60000).toISOString().slice(0, 16)}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                    />
                    {extendError && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400 font-medium">
                        {extendError}
                      </p>
                    )}
                  </div>

                  <div className="p-4 bg-shades-extra-light rounded-lg">
                    <p className="text-sm text-text-secondary mb-2">
                      <strong>LOC Details:</strong>
                    </p>
                    <ul className="text-xs text-text-secondary space-y-1">
                      <li>• ID: #{loc.id.toString()}</li>
                      <li>• Amount: {formatTokenAmount(loc.creditedAmount, creditedDecimals)} {loc.creditedAssetSymbol}</li>
                      <li>• Current Expiry: {formatDate(Number(loc.expiry))}</li>
                      <li>• Beneficiary: {formatAddress(loc.beneficiary)}</li>
                    </ul>
                  </div>

                  <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
                    <Button
                      variant="ghost"
                      onClick={() => setShowExtendModal(false)}
                      disabled={isExtending}
                      className="w-full sm:w-auto min-h-[48px]"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleExtend}
                      disabled={isExtending || !newExpiryDate}
                      className="w-full sm:w-auto min-h-[48px]"
                    >
                      {isExtending ? 'Extending...' : 'Extend LOC'}
                    </Button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>

    {/* ── Cancel LOC Modal (3a) ─────────────────────────────────────────── */}
    <Transition appear show={showCancelModal} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setShowCancelModal(false)}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4 text-center">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full sm:max-w-md transform overflow-hidden rounded-t-2xl sm:rounded-2xl bg-white dark:bg-gray-800 p-4 sm:p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-xl sm:text-2xl font-bold leading-6 text-gray-900 dark:text-white mb-4">
                  Cancel LOC #{locId.toString()}
                </Dialog.Title>
                <div className="space-y-4">
                  <InfoBox variant="warning" title="Are you sure?">
                    <p className="text-sm">
                      Cancelling this LOC will release the collateral back to your account.
                      The beneficiary will no longer be able to redeem this letter of credit.
                    </p>
                    <p className="text-sm mt-2 font-semibold">
                      Amount: {formatTokenAmount(loc.creditedAmount, creditedDecimals)} {loc.creditedAssetSymbol}
                    </p>
                  </InfoBox>
                  <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
                    <Button variant="ghost" onClick={() => setShowCancelModal(false)} disabled={isCancelling} className="w-full sm:w-auto min-h-[48px]">
                      Keep LOC
                    </Button>
                    <Button
                      onClick={handleCancel}
                      disabled={isCancelling}
                      className="w-full sm:w-auto min-h-[48px] bg-red-600 hover:bg-red-700 text-white border-red-600"
                    >
                      {isCancelling ? 'Cancelling...' : 'Cancel LOC'}
                    </Button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>

    {/* ── Add Collateral Modal (3b) ─────────────────────────────────────── */}
    <Transition appear show={showAddCollateralModal} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setShowAddCollateralModal(false)}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4 text-center">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full sm:max-w-md transform overflow-hidden rounded-t-2xl sm:rounded-2xl bg-white dark:bg-gray-800 p-4 sm:p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-xl sm:text-2xl font-bold leading-6 text-gray-900 dark:text-white mb-4">
                  Add Collateral — LOC #{locId.toString()}
                </Dialog.Title>
                <div className="space-y-4">
                  <InfoBox variant="info" title="Increase Collateral">
                    <p className="text-sm">
                      Adding more collateral improves the health of this LOC and reduces liquidation risk.
                    </p>
                    {locEvent && (
                      <p className="text-sm mt-1 text-text-secondary">
                        Collateral token:{' '}
                        <strong>
                          {Object.values(SUPPORTED_TOKENS).find(
                            t => t.address.toLowerCase() === locEvent.collateralTokenAddress.toLowerCase()
                          )?.symbol ?? locEvent.collateralTokenAddress.slice(0, 8) + '...'}
                        </strong>
                      </p>
                    )}
                    {health && (
                      <p className="text-sm mt-1">
                        Current health:{' '}
                        <span className={`font-semibold ${
                          health.color === 'green' ? 'text-green-600' :
                          health.color === 'yellow' ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {health.label} ({(health.collateralRatio * 100).toFixed(0)}% ratio)
                        </span>
                      </p>
                    )}
                  </InfoBox>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Amount to Add
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      placeholder="0.00"
                      value={addCollateralAmount}
                      onChange={(e) => { setAddCollateralAmount(e.target.value); setAddCollateralError('') }}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                    />
                    {addCollateralError && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400 font-medium">{addCollateralError}</p>
                    )}
                  </div>
                  <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
                    <Button variant="ghost" onClick={() => setShowAddCollateralModal(false)} disabled={isModifyingCollateral} className="w-full sm:w-auto min-h-[48px]">
                      Cancel
                    </Button>
                    <Button onClick={handleAddCollateral} disabled={isModifyingCollateral || !addCollateralAmount} className="w-full sm:w-auto min-h-[48px]">
                      {isModifyingCollateral ? 'Adding...' : 'Add Collateral'}
                    </Button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>

    {/* ── Remove Collateral Modal (MAP-19) ──────────────────────────────── */}
    <Transition appear show={showRemoveCollateralModal} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => setShowRemoveCollateralModal(false)}>
        <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/50" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4 text-center">
            <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <Dialog.Panel className="w-full sm:max-w-md transform overflow-hidden rounded-t-2xl sm:rounded-2xl bg-white dark:bg-gray-800 p-4 sm:p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-xl sm:text-2xl font-bold leading-6 text-gray-900 dark:text-white mb-4">
                  Remove Collateral — LOC #{locId.toString()}
                </Dialog.Title>
                <div className="space-y-4">
                  <InfoBox variant="warning" title="Reduce Collateral">
                    <p className="text-sm">
                      Removing collateral decreases the health of this LOC and increases liquidation risk. 
                      The system will prevent removal if it would breach the maintenance requirement.
                    </p>
                    {locEvent && (
                      <p className="text-sm mt-1 text-text-secondary">
                        Collateral token:{' '}
                        <strong>
                          {Object.values(SUPPORTED_TOKENS).find(
                            t => t.address.toLowerCase() === locEvent.collateralTokenAddress.toLowerCase()
                          )?.symbol ?? locEvent.collateralTokenAddress.slice(0, 8) + '...'}
                        </strong>
                      </p>
                    )}
                    {health && (
                      <p className="text-sm mt-1">
                        Current health:{' '}
                        <span className={`font-semibold ${
                          health.color === 'green' ? 'text-green-600' :
                          health.color === 'yellow' ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {health.label} ({(health.collateralRatio * 100).toFixed(0)}% ratio)
                        </span>
                        {' · '}Min: {(health.maintenanceFactor * 100).toFixed(0)}%
                      </p>
                    )}
                  </InfoBox>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                      Amount to Remove
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      placeholder="0.00"
                      value={removeCollateralAmount}
                      onChange={(e) => { setRemoveCollateralAmount(e.target.value); setRemoveCollateralError('') }}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-orange focus:border-brand-orange"
                    />
                    {removeCollateralError && (
                      <p className="mt-2 text-sm text-red-600 dark:text-red-400 font-medium">{removeCollateralError}</p>
                    )}
                  </div>
                  <div className="flex flex-col-reverse sm:flex-row gap-3 pt-4">
                    <Button variant="ghost" onClick={() => setShowRemoveCollateralModal(false)} disabled={isModifyingCollateral} className="w-full sm:w-auto min-h-[48px]">
                      Cancel
                    </Button>
                    <Button
                      onClick={handleRemoveCollateral}
                      disabled={isModifyingCollateral || !removeCollateralAmount}
                      className="w-full sm:w-auto min-h-[48px] bg-orange-600 hover:bg-orange-700 text-white border-orange-600"
                    >
                      {isModifyingCollateral ? 'Removing...' : 'Remove Collateral'}
                    </Button>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
    </>
  )
}

